/**
 * SAV Form - Multi-step After-Sales Service Form
 * Handles verification, product selection, problem description, and submission
 */



class SavForm {
  API_BASE_URL = "https://api-bops-7q4x2p9a.belvans.com";

  userEmail = ''
  orderName = ''

  constructor(element) {
    this.element = element;
    this.section = this.element.closest('[data-section-type="sav-form"]');
    this.currentStep = 0;
    this.totalSteps = 6;
    this.motionEnabled = this.section?.dataset.motionEnabled !== 'false';
    this.prefersReducedMotion = !this.motionEnabled || (
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false
    );
    this.stepAnimationTimeout = null;
    this.visualRefreshFrame = null;
    this.resizeDebounceTimer = null;
    this.feedbackHideTimers = {};
    this.availableProducts = [];
    this.productsLoadState = 'idle';
    this.exchangeCandidates = [];
    this.exchangeCandidatesLoadState = 'idle';
    this.formData = {
      channel: '',
      verified: false,
      serviceType: '',
      serviceReason: '',
      locale: '',
      problemCategory: '',
      problemDescription: '',
      selectedProduct: null,
      selectedExchangeProduct: null,
      photos: [],
      returnAddress1: '',
      returnAddress2: '',
      returnStreet: '',
      returnCity: '',
      returnZip: '',
      returnProvince: '',
      returnCountry: '',
      returnPhone: '',
      returnPhoneCountry: ''
    };

    // Get configuration from section settings
    this.config = {
      addressAutocompleteKey: this.section?.dataset.addressAutocompleteKey || ''
    };
    this.themeConfig = this.parseThemeConfig();

    this.init();
  }

  createDebugSnapshot(value) {
    if (value instanceof File) {
      return {
        name: value.name,
        size: value.size,
        type: value.type
      };
    }

    if (value instanceof FormData) {
      const entries = [];
      value.forEach((entryValue, key) => {
        entries.push({
          key,
          value: this.createDebugSnapshot(entryValue)
        });
      });
      return entries;
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.createDebugSnapshot(entry));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((snapshot, [key, entryValue]) => {
        snapshot[key] = this.createDebugSnapshot(entryValue);
        return snapshot;
      }, {});
    }

    return value;
  }

  getStorefrontLocale() {
    return (
      (window.Shopify && window.Shopify.locale) ||
      document.documentElement.lang ||
      'en'
    );
  }

  // Les listes de pays sont écrites en français dans le markup ; sur les autres
  // langues publiées, les noms sont localisés au chargement via Intl.DisplayNames.
  localizeCountrySelects() {
    const locale = String(this.getStorefrontLocale()).split('-')[0];
    if (!locale || locale === 'fr' || typeof Intl === 'undefined' || !Intl.DisplayNames) return;

    let regionNames;
    try {
      regionNames = new Intl.DisplayNames([locale], { type: 'region' });
    } catch (error) {
      return;
    }

    const localizeOptions = (select, getIso, buildLabel, updateValue) => {
      if (!select) return;
      const localized = [];
      Array.from(select.options).forEach((option) => {
        const iso = getIso(option);
        if (!iso) return;
        let name;
        try {
          name = regionNames.of(iso);
        } catch (error) {
          return;
        }
        if (!name || name === iso) return;
        option.textContent = buildLabel(option, name);
        if (updateValue) option.value = name;
        localized.push(option);
      });
      // Retrie alphabétiquement dans la langue du visiteur (les options non
      // localisées, comme le placeholder, restent en tête). Le drapeau en tête
      // de libellé est ignoré pour la comparaison.
      const nameOf = (option) => option.textContent.trim().replace(/^\S+\s+/, '');
      localized
        .sort((a, b) => nameOf(a).localeCompare(nameOf(b), locale))
        .forEach((option) => select.appendChild(option));
    };

    const flagOf = (option) => {
      const match = option.textContent.trim().match(/^\S+/);
      return match ? match[0] : '';
    };

    localizeOptions(
      this.returnCountryInput && this.returnCountryInput.tagName === 'SELECT' ? this.returnCountryInput : null,
      (option) => option.dataset.iso || '',
      (option, name) => `${flagOf(option)} ${name}`,
      true
    );

    localizeOptions(
      this.returnPhoneCountryInput,
      (option) => {
        const match = String(option.value).match(/^([A-Z]{2})\+/);
        return match ? match[1] : '';
      },
      (option, name) => {
        const dial = String(option.value).split('+')[1] || '';
        return `${flagOf(option)} ${name} +${dial}`;
      },
      false
    );
  }

  logApiRequest(step, input) {
    console.log(`[SAV][${step}] request`, this.createDebugSnapshot(input));
  }

  logApiResponse(step, response, data) {
    console.log(`[SAV][${step}] response`, {
      status: response?.status,
      ok: response?.ok,
      data: this.createDebugSnapshot(data)
    });
  }

   async init() {
    this.cacheElements();
    this.applyDynamicTexts();
    this.applyServiceTypeAvailability();
    this.bindEvents();
    this.handleChannelChange();
    this.syncServiceTypeUI();
    this.syncConditionalStepVisibility();
    this.localizeCountrySelects();
    this.initAddressAutocomplete();
    this.bindResizeHandler();
    this.animateStep(this.currentStep);
    this.updateButtons();
    this.requestVisualRefresh({ immediate: true });

  }

  parseThemeConfig() {
    const defaultConfig = {
      displayReturn: false,
      displayExchange: false,
      faqItems: [],
      messages: this.getDefaultMessages()
    };

    const configEl = this.section?.querySelector('[data-sav-theme-config]');
    if (!configEl) {
      return defaultConfig;
    }

    try {
      const parsed = JSON.parse(configEl.textContent || '{}');

      const faqItems = Array.isArray(parsed.faqItems)
        ? parsed.faqItems
            .map((item) => this.normalizeThemeFaq(item))
            .filter(Boolean)
        : [];

      const messages = {
        ...defaultConfig.messages,
        ...(parsed.messages || {})
      };

      return {
        displayReturn: Boolean(parsed.displayReturn),
        displayExchange: Boolean(parsed.displayExchange),
        faqItems,
        messages
      };
    } catch (error) {
      console.error('Failed to parse Theme Editor config:', error);
      return defaultConfig;
    }
  }

  getDefaultMessages() {
    return {
      noProductsConfigured: "Aucune montre SAV n'est configurée. Ajoutez des blocs \"Produit SAV\" dans l'éditeur de thème.",
      noProductSelected: "Veuillez sélectionner votre montre.",
      noFaqConfigured: "Aucune FAQ n'est configurée pour la montre sélectionnée.",
      purchaseChannelRequired: "Veuillez choisir une option avant de continuer.",
      orderNumberRequired: "Veuillez saisir votre numéro de commande.",
      emailRequired: "Veuillez saisir votre adresse e-mail.",
      emailInvalid: "Veuillez saisir une adresse e-mail valide.",
      identifierRequired: "Renseignez soit le numéro de commande boutique, soit le numéro de carte de garantie.",
      serviceTypeRequired: "Veuillez sélectionner le motif du service.",
      serviceReasonRequired: "Veuillez sélectionner le motif de votre demande.",
      serviceTypeReturnLabel: "Retour",
      serviceTypeExchangeLabel: "Échange",
      serviceTypeRepairLabel: "Réparation",
      serviceReasonReturnPlaceholder: "Sélectionnez un motif de retour",
      serviceReasonExchangePlaceholder: "Sélectionnez un motif d'échange",
      problemCategoryRequired: "Veuillez sélectionner la catégorie du problème.",
      problemDescriptionRequired: "Décrivez le problème en détail (au moins 20 caractères).",
      returnAddress1Required: "Veuillez saisir l'adresse (ligne 1).",
      returnCityRequired: "Veuillez saisir la ville.",
      returnZipRequired: "Veuillez saisir le code postal.",
      returnProvinceRequired: "Veuillez saisir la région / l'état.",
      returnCountryRequired: "Veuillez saisir le pays.",
      verificationCodeSent: "Un code de vérification a été envoyé à votre adresse e-mail.",
      verificationFailed: "La vérification a échoué. Veuillez réessayer.",
      orderNotFound: "Commande introuvable. Vérifiez le numéro saisi.",
      otpIncomplete: "Veuillez saisir le code complet à 6 chiffres.",
      otpInvalid: "Code de vérification invalide.",
      resendCodeSuccess: "Un nouveau code de vérification a été envoyé.",
      resendCodeFailed: "Impossible de renvoyer le code. Veuillez réessayer.",
      maxPhotosAllowed: "Vous pouvez ajouter jusqu'à 5 photos.",
      invalidPhotoType: "Formats autorisés : JPG, PNG et WEBP.",
      invalidPhotoSize: "Chaque fichier doit faire moins de 10 Mo.",
      noProductsFoundInOrders: "Aucune montre trouvée dans vos commandes.",
      loadProductsFailed: "Impossible de charger les montres.",
      exchangeProductRequired: "Veuillez sélectionner le produit d'échange souhaité.",
      noExchangeProductsFound: "Aucun produit d'échange au même prix n'est disponible pour cette montre.",
      loadExchangeProductsFailed: "Impossible de charger les produits d'échange.",
      exchangeProductsWaiting: "Sélectionnez d'abord la montre concernée pour afficher les produits d'échange disponibles.",
      exchangeProductsLoading: "Recherche des produits d'échange disponibles...",
      submissionFailed: "L'envoi de la demande a échoué. Veuillez réessayer.",
      photoUploadFailed: "L'envoi des photos a échoué.",
      buttonVerify: "Vérifier",
      buttonVerifyOtp: "Vérifier le code",
      buttonNext: "Suivant",
      buttonReview: "Vérifier",
      buttonSubmit: "Envoyer la demande",
      summaryOnlinePurchase: "Achat en ligne",
      summaryBoutiquePurchase: "Achat en boutique",
      summaryPhotosAttached: "photo(s) ajoutée(s)",
      faqCancelButton: "Annuler la demande",
      faqContinueButton: "Continuer ma demande"
    };
  }

  getMessage(key, fallback = '') {
    const value = this.themeConfig?.messages?.[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
    return fallback;
  }

  isReturnDisplayEnabled() {
    return Boolean(this.themeConfig?.displayReturn);
  }

  isExchangeDisplayEnabled() {
    return Boolean(this.themeConfig?.displayExchange);
  }

  hasServiceTypeChoice() {
    return this.isReturnDisplayEnabled() || this.isExchangeDisplayEnabled();
  }

  getAvailableServiceTypes() {
    const types = ['repair'];
    if (this.isReturnDisplayEnabled()) types.push('return');
    if (this.isExchangeDisplayEnabled()) types.push('exchange');
    return types;
  }

  getDefaultServiceType() {
    return 'repair';
  }

  applyServiceTypeAvailability() {
    if (!this.serviceTypeInputs?.length) {
      this.formData.serviceType = this.getDefaultServiceType();
      return;
    }

    const availableTypes = this.getAvailableServiceTypes();

    // Drop any selection that is no longer available (e.g. toggle was just turned off in editor).
    this.serviceTypeInputs.forEach((input) => {
      if (!availableTypes.includes(input.value)) {
        input.checked = false;
      }
    });

    const selectedInput = Array.from(this.serviceTypeInputs).find((input) => input.checked);

    if (selectedInput) {
      this.formData.serviceType = selectedInput.value;
    } else if (!this.hasServiceTypeChoice()) {
      // Repair-only mode: ensure the hidden repair input is checked so submit/validation work.
      const repairInput = Array.from(this.serviceTypeInputs).find((input) => input.value === 'repair');
      if (repairInput) repairInput.checked = true;
      this.formData.serviceType = this.getDefaultServiceType();
    } else {
      // Choice mode with nothing picked yet — leave empty until the user chooses.
      this.formData.serviceType = '';
    }
  }

  normalizeSku(value) {
    if (value == null) return '';
    return String(value).trim().toUpperCase();
  }

  normalizeThemeFaq(item) {
    if (!item || !item.question || !item.answer) {
      return null;
    }

    const isGlobal = Boolean(item.applyToAll);
    const collectionHandle = item.collectionHandle
      ? String(item.collectionHandle).trim().toLowerCase()
      : '';

    if (!isGlobal && !collectionHandle) {
      return null;
    }

    return {
      question: String(item.question),
      answer: String(item.answer),
      applyToAll: isGlobal,
      collectionHandle,
      isCollapsible: item.isCollapsible !== false,
      hostedVideoUrl: item.hostedVideoUrl ? String(item.hostedVideoUrl) : '',
      hostedVideoPoster: item.hostedVideoPoster ? String(item.hostedVideoPoster) : '',
      externalVideoType: item.externalVideoType ? String(item.externalVideoType) : '',
      externalVideoId: item.externalVideoId ? String(item.externalVideoId) : ''
    };
  }

  cacheElements() {
    // Steps
    this.steps = this.element.querySelectorAll('[data-sav-step]');
    this.progressSteps = this.element.querySelectorAll('[data-step]');
    this.progressLine = this.element.querySelector('[data-sav-progress-line]');

    // Buttons
    this.nextBtn = this.element.querySelector('[data-next-btn]');
    this.prevBtn = this.element.querySelector('[data-prev-btn]');

    // Step 0 elements
    this.step0 = this.element.querySelector('[data-sav-step="0"]');
    this.channelBlock = this.element.querySelector('[data-channel-block]');
    this.channelInputs = this.element.querySelectorAll('[data-channel-input]');
    this.onlineFields = this.element.querySelector('#online-fields');
    this.boutiqueFields = this.element.querySelector('#boutique-fields');
    this.orderNumberInput = this.element.querySelector('[data-order-number]');
    this.emailInput = this.element.querySelector('[data-email]');
    this.boutiqueOrderInput = this.element.querySelector('[data-boutique-order]');
    this.warrantyCardInput = this.element.querySelector('[data-warranty-card]');
    this.serviceTypeInputs = this.element.querySelectorAll('[data-service-type-input]');
    this.serviceReasonFields = this.element.querySelector('#service-reason-fields');
    this.serviceReasonSelect = this.element.querySelector('[data-service-reason-select]');
    this.otpSection = this.element.querySelector('#otp-section');
    this.otpInputs = this.element.querySelectorAll('[data-otp-digit]');
    this.resendOtpBtn = this.element.querySelector('[data-resend-otp]');

    // Step 1 elements
    this.productsList = this.element.querySelector('[data-products-list]');
    this.initialProductsMarkup = this.productsList ? this.productsList.innerHTML : '';
    this.exchangeProductsSection = this.element.querySelector('[data-exchange-products-section]');
    this.exchangeProductsList = this.element.querySelector('[data-exchange-products-list]');
    this.initialExchangeProductsMarkup = this.exchangeProductsList ? this.exchangeProductsList.innerHTML : '';

    // Step 2 elements
    this.faqSection = this.element.querySelector('[data-faq-section]');
    this.faqItems = this.element.querySelector('[data-faq-items]');
    this.faqActions = this.element.querySelector('[data-faq-actions]');
    this.faqCancelBtn = this.element.querySelector('[data-faq-cancel-btn]');
    this.faqContinueBtn = this.element.querySelector('[data-faq-continue-btn]');

    // Step 3 elements
    this.problemCategorySelect = this.element.querySelector('[data-problem-category]');
    this.problemDescriptionTextarea = this.element.querySelector('[data-problem-description]');
    this.photoInput = this.element.querySelector('[data-photo-input]');
    this.photoPreviews = this.element.querySelector('[data-photo-previews]');

    // Step 4 elements
    this.returnAddress1Input = this.element.querySelector('[data-return-address-1]');
    this.returnAddress2Input = this.element.querySelector('[data-return-address-2]');
    this.returnPhoneInput = this.element.querySelector('[data-return-phone]');
    this.returnPhoneCountryInput = this.element.querySelector('[data-return-phone-country]');
    this.returnCityInput = this.element.querySelector('[data-return-city]');
    this.returnZipInput = this.element.querySelector('[data-return-zip]');
    this.returnProvinceInput = this.element.querySelector('[data-return-province]');
    this.returnCountryInput = this.element.querySelector('[data-return-country]');

    // Step 5 elements
    this.summary = this.element.querySelector('[data-summary]');
    this.summaryChannel = this.element.querySelector('[data-summary-channel]');
    this.summaryServiceType = this.element.querySelector('[data-summary-service-type]');
    this.summaryServiceReasonSection = this.element.querySelector('[data-summary-service-reason-section]');
    this.summaryServiceReason = this.element.querySelector('[data-summary-service-reason]');
    this.summaryEmail = this.element.querySelector('[data-summary-email]');
    this.summaryWatch = this.element.querySelector('[data-summary-watch]');
    this.summaryExchangeProductSection = this.element.querySelector('[data-summary-exchange-product-section]');
    this.summaryExchangeProduct = this.element.querySelector('[data-summary-exchange-product]');
    this.summaryCategory = this.element.querySelector('[data-summary-category]');
    this.summaryDescription = this.element.querySelector('[data-summary-description]');
    this.summaryReturnAddressSection = this.element.querySelector('[data-summary-return-address-section]');
    this.summaryReturnAddress = this.element.querySelector('[data-summary-return-address]');
    this.summaryPhotos = this.element.querySelector('[data-summary-photos]');

    // States
    this.loadingState = this.element.querySelector('[data-loading-state]');
    this.successState = this.element.querySelector('[data-success-state]');
    this.requestNumber = this.element.querySelector('[data-request-number]');
    this.actions = this.element.querySelector('[data-actions]');
    this.feedbackTargets = {
      otpResend: this.element.querySelector('[data-feedback="otp-resend"]'),
      formActions: this.element.querySelector('[data-feedback="form-actions"]')
    };
  }

  applyDynamicTexts() {
    if (this.faqCancelBtn) {
      this.faqCancelBtn.textContent = this.getMessage('faqCancelButton', this.faqCancelBtn.textContent || 'Annuler la demande');
    }
    if (this.faqContinueBtn) {
      this.faqContinueBtn.textContent = this.getMessage('faqContinueButton', this.faqContinueBtn.textContent || 'Continuer ma demande');
    }
  }

  bindEvents() {
    // Channel selection
    this.channelInputs.forEach(input => {
      input.addEventListener('change', () => this.handleChannelChange());
    });

    // Editing any identifier invalidates a pending or completed verification
    [this.orderNumberInput, this.emailInput, this.boutiqueOrderInput, this.warrantyCardInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', () => this.resetVerification());
    });

    this.serviceTypeInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleServiceTypeChange(input));
    });

    if (this.serviceReasonSelect) {
      this.serviceReasonSelect.addEventListener('change', () => this.handleServiceReasonChange());
    }

    // Navigation buttons
    this.nextBtn.addEventListener('click', () => this.handleNext());
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.handlePrev());
    }

    // OTP inputs
    this.otpInputs.forEach((input, index) => {
      input.addEventListener('input', (e) => this.handleOtpInput(e, index));
      input.addEventListener('keydown', (e) => this.handleOtpKeydown(e, index));
      input.addEventListener('paste', (e) => this.handleOtpPaste(e));
    });

    // Resend OTP
    if (this.resendOtpBtn) {
      this.resendOtpBtn.addEventListener('click', () => this.resendOtp());
    }

    // Photo upload
    if (this.photoInput) {
      this.photoInput.addEventListener('change', (e) => this.handlePhotoSelect(e));
    }

    // FAQ step actions
    if (this.faqCancelBtn) {
      this.faqCancelBtn.addEventListener('click', () => this.handleFaqCancel());
    }

    if (this.faqContinueBtn) {
      this.faqContinueBtn.addEventListener('click', () => this.handleFaqContinue());
    }
  }

  resetVerification() {
    this.clearFieldError('order-number');
    this.clearFieldError('identifier');

    const otpVisible = this.otpSection && this.otpSection.style.display === 'block';
    if (!otpVisible && !this.formData.verified) return;

    this.formData.verified = false;
    if (this.otpInputs) {
      this.otpInputs.forEach((input) => {
        input.value = '';
      });
    }
    if (this.otpSection) {
      this.otpSection.style.display = 'none';
    }
    this.clearFeedback();
    this.updateButtons();
  }

  handleChannelChange() {
    const selectedInput = this.element.querySelector('[data-channel-input]:checked');
    const selectedChannel = selectedInput ? selectedInput.value : '';
    this.formData.channel = selectedChannel;
    this.resetVerification();
    if (selectedChannel) {
      const channelError = this.element.querySelector('[data-error="purchase-channel"]');
      if (channelError) {
        channelError.textContent = '';
        channelError.classList.remove('is-visible');
      }
    }

    if (selectedChannel === 'online') {
      this.toggleChannelFields(this.onlineFields, true);
      this.toggleChannelFields(this.boutiqueFields, false);
    } else if (selectedChannel === 'boutique') {
      this.toggleChannelFields(this.onlineFields, false);
      this.toggleChannelFields(this.boutiqueFields, true);
    } else {
      this.toggleChannelFields(this.onlineFields, false);
      this.toggleChannelFields(this.boutiqueFields, false);
    }
  }

  toggleChannelFields(element, shouldShow) {
    if (!element) return;

    if (this.prefersReducedMotion) {
      element.classList.toggle('is-visible', shouldShow);
      element.style.maxHeight = shouldShow ? 'none' : '0px';
      element.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      return;
    }

    if (shouldShow) {
      element.classList.add('is-visible');
      element.setAttribute('aria-hidden', 'false');
      element.style.maxHeight = '0px';
      window.requestAnimationFrame(() => {
        element.style.maxHeight = `${element.scrollHeight}px`;
      });

      if (element._savChannelTimer) {
        window.clearTimeout(element._savChannelTimer);
      }

      element._savChannelTimer = window.setTimeout(() => {
        if (element.classList.contains('is-visible')) {
          element.style.maxHeight = 'none';
        }
      }, 320);
      return;
    }

    if (element._savChannelTimer) {
      window.clearTimeout(element._savChannelTimer);
    }

    element.style.maxHeight = element.style.maxHeight === 'none' ? `${element.scrollHeight}px` : element.style.maxHeight || `${element.scrollHeight}px`;
    void element.offsetHeight;
    element.classList.remove('is-visible');
    element.setAttribute('aria-hidden', 'true');
    element.style.maxHeight = '0px';
  }

  getServiceReasonOptions(serviceType) {
    const options = {
      return: [
        { value: 'changed_mind', label: 'Je ne souhaite plus conserver le produit' },
        { value: 'not_as_expected', label: "Le produit ne correspond pas à mes attentes" },
        { value: 'received_damaged', label: 'Le produit a été reçu endommagé' },
        { value: 'incorrect_item', label: 'Je n’ai pas reçu le bon produit' },
        { value: 'other_return', label: 'Autre motif de retour' }
      ],
      exchange: [
        { value: 'different_model', label: 'Je souhaite un autre modèle' },
        { value: 'different_finish', label: 'Je souhaite une autre couleur / finition' },
        { value: 'received_damaged', label: 'Le produit reçu présente un défaut' },
        { value: 'incorrect_item', label: 'Je souhaite échanger un produit incorrect' },
        { value: 'other_exchange', label: 'Autre motif d’échange' }
      ]
    };

    return options[serviceType] || [];
  }

  getSelectedServiceType() {
    const selectedInput = Array.from(this.serviceTypeInputs || []).find((input) => input.checked);
    const value = selectedInput ? String(selectedInput.value || '').trim() : '';
    const availableTypes = this.getAvailableServiceTypes();
    if (availableTypes.includes(value)) return value;
    // Repair-only: fall back to default. Choice mode: leave empty so validation can prompt the user.
    return this.hasServiceTypeChoice() ? '' : this.getDefaultServiceType();
  }

  requiresDetailedServiceReason(serviceType = this.formData.serviceType) {
    return serviceType === 'return' || serviceType === 'exchange';
  }

  shouldSkipReturnAddressStep() {
    return this.formData.serviceType === 'return';
  }

  shouldSkipProblemDetailsStep() {
    return this.formData.serviceType === 'return' || this.formData.serviceType === 'exchange';
  }

  getVisibleStepIndices() {
    return Array.from(this.steps || [])
      .map((_, index) => index)
      .filter((index) => !this.isStepHidden(index));
  }

  hasTipsForSelectedProduct() {
    const faqItems = Array.isArray(this.themeConfig?.faqItems) ? this.themeConfig.faqItems : [];
    if (faqItems.length === 0) return false;
    if (!this.formData.selectedProduct) return true;
    return this.getThemeFaqItemsForSelectedProduct().length > 0;
  }

  isStepHidden(stepIndex) {
    return (
      (stepIndex === 2 && !this.hasTipsForSelectedProduct()) ||
      (stepIndex === 3 && this.shouldSkipProblemDetailsStep()) ||
      (stepIndex === 4 && this.shouldSkipReturnAddressStep())
    );
  }

  getNextStepIndex(fromStep = this.currentStep) {
    const visibleSteps = this.getVisibleStepIndices();
    const currentPosition = visibleSteps.indexOf(fromStep);
    if (currentPosition === -1) {
      const nextVisibleStep = visibleSteps.find((stepIndex) => stepIndex > fromStep);
      return nextVisibleStep ?? visibleSteps[visibleSteps.length - 1] ?? fromStep;
    }

    return visibleSteps[currentPosition + 1] ?? fromStep;
  }

  getPreviousStepIndex(fromStep = this.currentStep) {
    const visibleSteps = this.getVisibleStepIndices();
    const currentPosition = visibleSteps.indexOf(fromStep);
    if (currentPosition === -1) {
      const previousVisibleSteps = visibleSteps.filter((stepIndex) => stepIndex < fromStep);
      return previousVisibleSteps[previousVisibleSteps.length - 1] ?? visibleSteps[0] ?? fromStep;
    }

    return visibleSteps[currentPosition - 1] ?? fromStep;
  }

  getServiceTypeLabel(serviceType = this.formData.serviceType) {
    const labels = {
      return: this.getMessage('serviceTypeReturnLabel', 'Retour'),
      exchange: this.getMessage('serviceTypeExchangeLabel', 'Échange'),
      repair: this.getMessage('serviceTypeRepairLabel', 'Réparation')
    };

    return labels[serviceType] || '';
  }

  getServiceReasonLabel(serviceType = this.formData.serviceType, reasonValue = this.formData.serviceReason) {
    if (!serviceType || !reasonValue) {
      return '';
    }

    const option = this.getServiceReasonOptions(serviceType).find((item) => item.value === reasonValue);
    return option ? option.label : reasonValue;
  }

  populateServiceReasonOptions() {
    if (!this.serviceReasonSelect) {
      return;
    }

    const serviceType = this.formData.serviceType;
    const options = this.getServiceReasonOptions(serviceType);
    const previousValue = this.formData.serviceReason || this.serviceReasonSelect.value || '';
    const placeholder = serviceType === 'return'
      ? this.getMessage('serviceReasonReturnPlaceholder', 'Sélectionnez un motif de retour')
      : this.getMessage('serviceReasonExchangePlaceholder', "Sélectionnez un motif d'échange");

    const optionMarkup = options.map((option) => `
      <option value="${this.escapeHtml(option.value)}">${this.escapeHtml(option.label)}</option>
    `).join('');

    this.serviceReasonSelect.innerHTML = `
      <option value="">${this.escapeHtml(placeholder)}</option>
      ${optionMarkup}
    `;

    const hasPreviousValue = options.some((option) => option.value === previousValue);
    this.serviceReasonSelect.value = hasPreviousValue ? previousValue : '';
    this.formData.serviceReason = this.serviceReasonSelect.value;
  }

  clearFieldError(fieldName) {
    const borderSoft = this.getStyleValue('--sav-lux-border-soft', '#dfdfdf');
    const errorEl = this.element.querySelector(`[data-error="${fieldName}"]`);
    if (!errorEl) {
      return;
    }

    errorEl.textContent = '';
    errorEl.classList.remove('is-visible');

    const input = errorEl.previousElementSibling;
    const isFormControl = input && (
      input.matches('input, select, textarea') ||
      input.classList.contains('sav-form__input') ||
      input.classList.contains('sav-form__select') ||
      input.classList.contains('sav-form__textarea')
    );

    if (isFormControl) {
      input.classList.remove('is-error');
      input.style.borderColor = borderSoft;
      input.style.boxShadow = 'none';
    }
  }

  clearReturnAddressData({ clearInputs = false } = {}) {
    this.formData.returnAddress1 = '';
    this.formData.returnAddress2 = '';
    this.formData.returnStreet = '';
    this.formData.returnCity = '';
    this.formData.returnZip = '';
    this.formData.returnProvince = '';
    this.formData.returnCountry = '';
    this.formData.returnPhone = '';
    this.formData.returnPhoneCountry = '';

    if (!clearInputs) {
      return;
    }

    if (this.returnAddress1Input) this.returnAddress1Input.value = '';
    if (this.returnAddress2Input) this.returnAddress2Input.value = '';
    if (this.returnCityInput) this.returnCityInput.value = '';
    if (this.returnZipInput) this.returnZipInput.value = '';
    if (this.returnProvinceInput) this.returnProvinceInput.value = '';
    if (this.returnCountryInput) this.returnCountryInput.value = '';
    if (this.returnPhoneInput) this.returnPhoneInput.value = '';
    if (this.returnPhoneCountryInput) {
      this.returnPhoneCountryInput.value = 'FR+33';
      if (!this.returnPhoneCountryInput.value && this.returnPhoneCountryInput.options.length > 0) {
        this.returnPhoneCountryInput.selectedIndex = 0;
      }
    }
  }

  clearProblemDetailsData({ clearInputs = false } = {}) {
    this.formData.problemCategory = '';
    this.formData.problemDescription = '';
    this.formData.photos = [];

    if (!clearInputs) {
      return;
    }

    if (this.problemCategorySelect) this.problemCategorySelect.selectedIndex = 0;
    if (this.problemDescriptionTextarea) this.problemDescriptionTextarea.value = '';
    if (this.photoInput) this.photoInput.value = '';
    if (this.photoPreviews) this.photoPreviews.innerHTML = '';
  }

  syncServiceReasonField() {
    const shouldShow = this.requiresDetailedServiceReason();

    if (shouldShow) {
      this.populateServiceReasonOptions();
    } else {
      this.formData.serviceReason = '';
      if (this.serviceReasonSelect) {
        this.serviceReasonSelect.innerHTML = '<option value="">' + this.escapeHtml(this.getMessage('serviceReasonPlaceholder', 'Sélectionnez un motif')) + '</option>';
        this.serviceReasonSelect.value = '';
      }
      this.clearFieldError('service-reason');
    }

    this.toggleChannelFields(this.serviceReasonFields, shouldShow);
  }

  syncConditionalStepVisibility() {
    const shouldHideProblemDetailsStep = this.shouldSkipProblemDetailsStep();
    const shouldHideAddressStep = this.shouldSkipReturnAddressStep();
    const problemDetailsStep = this.steps?.[3];
    const problemDetailsProgressStep = this.progressSteps?.[3];
    const addressStep = this.steps?.[4];
    const addressProgressStep = this.progressSteps?.[4];

    if (problemDetailsStep) {
      problemDetailsStep.hidden = shouldHideProblemDetailsStep;
    }

    if (problemDetailsProgressStep) {
      problemDetailsProgressStep.hidden = shouldHideProblemDetailsStep;
    }

    if (addressStep) {
      addressStep.hidden = shouldHideAddressStep;
    }

    if (addressProgressStep) {
      addressProgressStep.hidden = shouldHideAddressStep;
    }

    if (shouldHideProblemDetailsStep) {
      this.clearProblemDetailsData({ clearInputs: true });
      this.clearFieldError('problem-category');
      this.clearFieldError('problem-description');
      this.clearFieldError('photos');
    }

    if (shouldHideAddressStep) {
      this.clearReturnAddressData({ clearInputs: true });
      this.clearFieldError('return-address-1');
      this.clearFieldError('return-city');
      this.clearFieldError('return-zip');
      this.clearFieldError('return-province');
      this.clearFieldError('return-country');
      this.clearFieldError('return-phone');
      this.clearFieldError('return-phone-country');
    }

    if (this.summaryReturnAddressSection) {
      this.summaryReturnAddressSection.hidden = shouldHideAddressStep;
    }

    const summaryCategorySection = this.summaryCategory?.closest('.sav-summary__section');
    if (summaryCategorySection) {
      summaryCategorySection.hidden = shouldHideProblemDetailsStep;
    }

    const summaryDescriptionSection = this.summaryDescription?.closest('.sav-summary__section');
    if (summaryDescriptionSection) {
      summaryDescriptionSection.hidden = shouldHideProblemDetailsStep;
    }

    const summaryPhotosSection = this.summaryPhotos?.closest('.sav-summary__section');
    if (summaryPhotosSection) {
      summaryPhotosSection.hidden = shouldHideProblemDetailsStep;
    }

    this.syncExchangeProductsVisibility();
    this.updateButtons();
    this.updateProgress();
    this.requestVisualRefresh();
  }

  syncExchangeProductsVisibility() {
    if (!this.exchangeProductsSection) {
      return;
    }

    const shouldShow =
      this.formData.serviceType === 'exchange' && Boolean(this.formData.selectedProduct);

    this.exchangeProductsSection.hidden = !shouldShow;

    if (!shouldShow && this.exchangeProductsList) {
      this.exchangeProductsList.innerHTML = this.initialExchangeProductsMarkup;
    }
  }

  syncServiceTypeUI() {
    this.applyServiceTypeAvailability();
    this.syncChannelBlockVisibility();
    this.applyForcedChannelForServiceType();
    this.syncServiceReasonField();
  }

  syncChannelBlockVisibility() {
    if (!this.step0) return;
    if (this.step0.dataset.needsServiceType !== 'true') {
      this.step0.dataset.serviceTypeSelected = 'true';
      return;
    }
    this.step0.dataset.serviceTypeSelected = this.formData.serviceType ? 'true' : 'false';
  }

  isChannelForcedOnline(serviceType = this.formData.serviceType) {
    return serviceType === 'return' || serviceType === 'exchange';
  }

  applyForcedChannelForServiceType() {
    if (!this.step0) return;

    const serviceType = this.formData.serviceType || '';
    this.step0.dataset.serviceType = serviceType;

    if (!this.isChannelForcedOnline(serviceType)) {
      // For repair (or empty) we leave the user's channel pick alone.
      return;
    }

    // Force "online" — return/exchange only make sense for online orders.
    let changed = false;
    this.channelInputs.forEach((input) => {
      const shouldBeChecked = input.value === 'online';
      if (input.checked !== shouldBeChecked) {
        input.checked = shouldBeChecked;
        changed = true;
      }
    });
    if (changed || this.formData.channel !== 'online') {
      this.handleChannelChange();
    }
    this.clearFieldError('purchase-channel');
  }

  handleServiceTypeChange(changedInput) {
    const isChecked = Boolean(changedInput?.checked);

    this.serviceTypeInputs.forEach((input) => {
      if (input !== changedInput) {
        input.checked = false;
      }
    });

    if (!isChecked && changedInput) {
      changedInput.checked = false;
    }

    this.formData.serviceType = this.getSelectedServiceType();

    if (this.formData.serviceType) {
      this.clearFieldError('service-type');
    }

    if (this.formData.serviceType !== 'exchange') {
      this.clearExchangeSelection({ resetMarkup: true });
    }

    this.syncChannelBlockVisibility();
    this.applyForcedChannelForServiceType();
    this.syncServiceReasonField();
    this.syncConditionalStepVisibility();

    if (this.formData.serviceType === 'exchange' && this.formData.selectedProduct) {
      this.loadExchangeCandidates();
    }
  }

  handleServiceReasonChange() {
    this.formData.serviceReason = this.serviceReasonSelect?.value || '';

    if (this.formData.serviceReason) {
      this.clearFieldError('service-reason');
    }
  }

  clearExchangeSelection({ resetMarkup = false } = {}) {
    this.formData.selectedExchangeProduct = null;
    this.exchangeCandidates = [];
    this.exchangeCandidatesLoadState = 'idle';

    if (resetMarkup && this.exchangeProductsList) {
      this.exchangeProductsList.innerHTML = this.initialExchangeProductsMarkup;
    }

    const exchangeError = this.element.querySelector('[data-error="exchange-selection"]');
    if (exchangeError) {
      exchangeError.textContent = '';
      exchangeError.classList.remove('is-visible');
    }
  }

async handleNext() {
  this.clearFeedback();

  const isValid = await this.validateStep(this.currentStep);
  if (!isValid) return;

  if (this.currentStep === 0) {
    // OTP section is visible but not yet verified — verify OTP
    if (!this.formData.verified && this.otpSection?.style.display === 'block') {
      await this.verifyOtp();
      return;
    }

    // OTP not yet requested — trigger verification
    if (!this.formData.verified) {
      await this.handleVerification();
      return;
    }
  }

  if (this.currentStep === 1 && !this.isStepHidden(2)) {
    await this.loadFaq();
  }

  const nextStep = this.getNextStepIndex(this.currentStep);

  if (
    this.currentStep === 4 ||
    (this.currentStep === 3 && nextStep === 5)
  ) {
    this.populateSummary();
  }

  if (this.currentStep === 5) {
    await this.submitForm();
    return;
  }

  this.currentStep = nextStep;
  this.showStep(this.currentStep);
} 

  handlePrev() {
    if (this.currentStep > 0) {
      this.currentStep = this.getPreviousStepIndex(this.currentStep);
      this.showStep(this.currentStep);
    }
  }

  handleFaqCancel() {
    this.resetRequestFlow();
  }

  handleFaqContinue() {
    if (this.currentStep !== 2) {
      return;
    }

    const nextStep = this.getNextStepIndex(this.currentStep);
    if (nextStep === 5) {
      this.populateSummary();
    }

    this.currentStep = nextStep;
    this.showStep(this.currentStep);
  }

  showStep(stepIndex) {
    const targetStep = this.isStepHidden(stepIndex)
      ? this.getPreviousStepIndex(stepIndex)
      : stepIndex;

    if (!this.steps[targetStep]) {
      return;
    }

    this.clearFeedback();

    // Hide all steps
    this.steps.forEach(step => {
      step.classList.remove('is-active', 'is-entering');
    });

    // Show current step
    this.currentStep = targetStep;
    const activeStep = this.steps[targetStep]; 
    activeStep.classList.add('is-active');

    this.animateStep(targetStep);

    // Update buttons
    this.updateButtons();
    this.requestVisualRefresh();

    // Scroll to top
    this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  animateStep(stepIndex) {
    const step = this.steps[stepIndex];
    if (!step || this.prefersReducedMotion) {
      return;
    }

    step.classList.remove('is-entering');
    void step.offsetWidth;
    step.classList.add('is-entering');

    if (this.stepAnimationTimeout) {
      window.clearTimeout(this.stepAnimationTimeout);
    }

    this.stepAnimationTimeout = window.setTimeout(() => {
      step.classList.remove('is-entering');
    }, 360);

    const motionTargets = step.querySelectorAll('.sav-form__group, .sav-radio, .sav-product, .sav-faq__item, .sav-faq-actions .sav-btn, .sav-summary__section');
    this.staggerIn(motionTargets, 28, 40);
  }

  staggerIn(elements, delayStep = 55, initialDelay = 0) {
    if (this.prefersReducedMotion) {
      return;
    }

    Array.from(elements).forEach((element, index) => {
      element.style.animation = 'none';
      void element.offsetWidth;
      element.style.animation = 'savItemIn 280ms ease-out forwards';
      element.style.animationDelay = `${initialDelay + index * delayStep}ms`;
    });
  }

  requestVisualRefresh(options = {}) {
    const { immediate = false } = options;

    const runRefresh = () => {
      this.applyStyles();
      this.updateProgress();
    };

    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      runRefresh();
      return;
    }

    if (immediate) {
      if (this.visualRefreshFrame) {
        window.cancelAnimationFrame(this.visualRefreshFrame);
        this.visualRefreshFrame = null;
      }
      runRefresh();
      return;
    }

    if (this.visualRefreshFrame) {
      return;
    }

    this.visualRefreshFrame = window.requestAnimationFrame(() => {
      this.visualRefreshFrame = null;
      runRefresh();
    });
  }

  bindResizeHandler() {
    if (this._resizeBound || typeof window === 'undefined') {
      return;
    }

    this._resizeBound = true;
    window.addEventListener('resize', () => {
      if (this.resizeDebounceTimer) {
        window.clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = window.setTimeout(() => {
        this.resizeDebounceTimer = null;
        this.requestVisualRefresh({ immediate: true });
      }, 150);
    });
  }

 applyStyles() {
    const root = this.section || this.element;
    if (!root || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
      return;
    }

    const rootStyles = window.getComputedStyle(root);
    const cssVar = (name, fallback = '') => {
      const value = rootStyles.getPropertyValue(name);
      return value && value.trim() ? value.trim() : fallback;
    };

    const primary = '#800000';
    const primaryDark = '#5a0000';
    const buttonColor = '#000000';
    const buttonHover = '#1a1a1a';
    const radioBorder = '#000000';
    const radioHoverBackground = '#80000010';
    const radioDefaultBackground = '#ffffff';
    const error = cssVar('--sav-error', '#800000');
    const success = cssVar('--sav-success', '#2D7F3E');
    const borderSoft = cssVar('--sav-lux-border-soft', '#dfdfdf');
    const border = cssVar('--sav-lux-border', '#cfcfcf');
    const surface = cssVar('--sav-surface', '#f7f7f7');
    const surfaceElevated = cssVar('--sav-surface-elevated', '#ffffff');
    const cardRadius = cssVar('--sav-card-radius', '0px');
    const inputRadius = cssVar('--sav-input-radius', '0px');
    const buttonRadius = cssVar('--sav-button-radius', '0px');
    const formWidth = cssVar('--sav-form-width', '860px');
    const formPadding = cssVar('--sav-form-padding', '34px');
    const bodySize = cssVar('--sav-body-size', '16px');
    const labelSize = cssVar('--sav-label-size', '15px');
    const buttonSize = cssVar('--sav-button-size', '16px');
    const buttonTransform = cssVar('--sav-button-transform', 'none');
    const faqQuestionSize = cssVar('--sav-faq-question-size', '16px');
    const summaryLabelSize = cssVar('--sav-summary-label-size', '11px');
    const progressCircleSize = cssVar('--sav-progress-circle-size', '38px');
    const headingFontFamily = cssVar('--sav-heading-font-family', 'inherit');
    const headingFontWeight = cssVar('--sav-heading-font-weight', 'inherit');
    const headingFontStyle = cssVar('--sav-heading-font-style', 'inherit');
    const bodyFontFamily = cssVar('--sav-body-font-family', 'inherit');
    const bodyFontWeight = cssVar('--sav-body-font-weight', 'inherit');
    const bodyFontStyle = cssVar('--sav-body-font-style', 'inherit');
    const buttonFontStack = cssVar('--BTN-FONT-STACK', bodyFontFamily);
    const buttonFontStyle = cssVar('--BTN-FONT-STYLE', 'normal');
    const buttonFontWeight = cssVar('--BTN-FONT-WEIGHT', '500');
    const buttonFontSize = cssVar('--BTN-FONT-SIZE', buttonSize);
    const buttonLetterSpacing = cssVar('--BTN-LETTER-SPACING', '0.005em');
    const buttonUppercase = cssVar('--BTN-UPPERCASE', buttonTransform);
    const colorText = cssVar('--COLOR-TEXT', cssVar('--COLOU-TEXT', '#1c1c1c'));
    const colorTextA50 = cssVar('--COLOR-TEXT-A50', cssVar('--COLOU-TEXT-A50', 'rgba(0, 0, 0, 0.5)'));
    const colorTextA80 = cssVar('--COLOR-TEXT-A80', cssVar('--COLOU-TEXT-A80', 'rgba(0, 0, 0, 0.8)'));
    const motionFactor = parseFloat(cssVar('--sav-motion-factor', '1')) || 1;
    const videoRadiusValue = (() => {
      const numeric = parseFloat(cardRadius);
      if (Number.isNaN(numeric)) return cardRadius;
      return `${numeric * 0.75}px`;
    })();

    const isMobile = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 749px)').matches
      : false;
    const circleSize = isMobile ? '40px' : progressCircleSize;
    const formPaddingValue = isMobile ? '1rem' : formPadding;

    const setStyles = (el, styles) => {
      if (!el) return;
      Object.entries(styles).forEach(([prop, value]) => {
        if (value === undefined || value === null) return;
        el.style[prop] = value;
      });
    };

    const styleButtonGroup = (container) => {
      if (!container || typeof window.getComputedStyle !== 'function') return;
      const buttons = Array.from(container.querySelectorAll('.sav-btn'));
      const visibleButtons = buttons.filter((btn) => (
        window.getComputedStyle(btn).display !== 'none'
      ));
      const isRow = visibleButtons.length > 1;
      setStyles(container, {
        display: 'flex',
        flexDirection: isRow ? 'row' : 'column',
        alignItems: 'stretch',
        gap: '1rem'
      });
      visibleButtons.forEach((btn) => {
        setStyles(btn, {
          flex: isRow ? '1 1 0' : '',
          width: '100%'
        });
      });
    };

    const bindOnce = (el, key, handler) => {
      if (!el || !el.dataset) return;
      if (el.dataset[key] === 'true') return;
      el.dataset[key] = 'true';
      handler();
    };

    setStyles(root, {
      position: 'relative',
      overflow: 'hidden',
      isolation: 'isolate',
      background: surface
    });

    setStyles(this.element, {
      maxWidth: formWidth,
      margin: '0 auto',
      padding: formPaddingValue,
      borderRadius: isMobile ? '0' : cardRadius,
      border: isMobile ? 'none' : `1px solid ${borderSoft}`,
      background: isMobile ? 'transparent' : surfaceElevated,
      boxShadow: 'none',
      position: 'relative',
      overflow: 'hidden',
      zIndex: '1',
      fontFamily: bodyFontFamily,
      fontStyle: bodyFontStyle,
      fontWeight: bodyFontWeight,
      fontSize: bodySize
    });

    this.element.querySelectorAll('.sav-form__header').forEach((header) => {
      setStyles(header, {
        position: 'relative',
        marginBottom: '2rem',
        paddingBottom: '1.75rem'
      });
    });

    this.element.querySelectorAll('.sav-form__header .heading-large').forEach((heading) => {
      setStyles(heading, {
        margin: '0'
      });
    });

    this.element.querySelectorAll('.sav-form__header .body-large').forEach((body) => {
      setStyles(body, {
        maxWidth: '60ch',
        marginLeft: 'auto',
        marginRight: 'auto'
      });
    });

    this.element.querySelectorAll('.sav-progress').forEach((progress) => {
      setStyles(progress, {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '2rem',
        position: 'relative',
        padding: '0'
      });
    });

    if (this.progressLine) {
      setStyles(this.progressLine, {
        position: 'absolute',
        top: `calc(${circleSize} / 2)`,
        left: '0',
        right: 'auto',
        width: '0',
        height: '1px',
        background: primary,
        transition: `width ${0.3 * motionFactor}s ease`,
        zIndex: '1'
      });
    }

    this.element.querySelectorAll('.sav-progress__step').forEach((step) => {
      setStyles(step, {
        position: 'relative',
        zIndex: '2',
        display: step.hidden ? 'none' : 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 0',
        minWidth: '0',
        gap: '0.55rem',
        transform: 'translateY(0)'
      });
    });

    this.element.querySelectorAll('.sav-progress__circle').forEach((circle) => {
      const step = circle.closest('.sav-progress__step');
      const isActive = step?.classList.contains('is-active');
      const isCompleted = step?.classList.contains('is-completed');
      setStyles(circle, {
        width: circleSize,
        height: circleSize,
        borderRadius: '50%',
        background: (isActive || isCompleted) ? primary : surfaceElevated,
        border: (isActive || isCompleted) ? `2px solid ${primary}` : `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'none',
        transition: `background-color ${0.25 * motionFactor}s ease, border-color ${0.25 * motionFactor}s ease, color ${0.25 * motionFactor}s ease`,
        color: (isActive || isCompleted) ? '#ffffff' : colorText
      });
    });

    this.element.querySelectorAll('.sav-progress__number').forEach((number) => {
      const step = number.closest('.sav-progress__step');
      const isActive = step?.classList.contains('is-active');
      const isCompleted = step?.classList.contains('is-completed');
      setStyles(number, {
        fontSize: '0.72rem',
        lineHeight: '1',
        fontWeight: '500',
        color: (isActive || isCompleted) ? '#ffffff' : '#999999'
      });
    });

    this.element.querySelectorAll('.sav-progress__label').forEach((label) => {
      const step = label.closest('.sav-progress__step');
      const isActive = step?.classList.contains('is-active');
      const isCompleted = step?.classList.contains('is-completed');
      setStyles(label, {
        fontSize: '0.7rem',
        textAlign: 'center',
        textTransform: 'none',
        letterSpacing: '0.02em',
        color: (isActive || isCompleted) ? primary : colorTextA50,
        fontWeight: isActive ? '600' : 'normal',
        transition: `color ${0.3 * motionFactor}s ease`,
        display: isMobile ? 'none' : ''
      });
    });

    this.element.querySelectorAll('.sav-step__title').forEach((title) => {
      setStyles(title, {
        display: 'flex',
        alignItems: 'center',
        gap: '0.45rem',
        marginBottom: '1.5rem'
      });
    });

    this.element.querySelectorAll('.sav-form__group').forEach((group) => {
      setStyles(group, { marginBottom: '1.5rem' });
    });

    this.element.querySelectorAll('.sav-form__container').forEach((container) => {
      setStyles(container, { position: 'relative' });
    });

    this.element.querySelectorAll('.sav-form__label').forEach((label) => {
      setStyles(label, {
        display: 'block',
        marginBottom: '0.55rem',
        fontSize: labelSize,
        letterSpacing: '0',
        fontWeight: '500',
        color: colorText
      });
    });

this.element.querySelectorAll('.sav-form__input, .sav-form__select, .sav-form__textarea').forEach((input) => {
      setStyles(input, {
        width: '100%',
        padding: '0.95rem 1rem',
        border: `1px solid ${borderSoft}`,
        borderRadius: inputRadius,
        fontSize: '1rem',
        lineHeight: '1.45',
        transition: `border-color ${0.2 * motionFactor}s ease, box-shadow ${0.2 * motionFactor}s ease`,
        background: '#fff',
        color: colorText,
        boxShadow: 'none'
      });

      if (input.classList.contains('is-error')) {
        input.style.border = `1px solid ${error}`;
      }

      bindOnce(input, 'savStyleBound', () => {
        input.addEventListener('focus', () => {
          input.style.border = `1px solid ${primary}`;
          input.style.outline = 'none';
          input.style.boxShadow = `inset 0 0 0 1px ${primary}`;
        });

        input.addEventListener('blur', () => {
          input.style.boxShadow = 'none';
          if (input.classList.contains('is-error')) {
            input.style.border = `1px solid ${error}`;
          } else {
            input.style.border = `1px solid ${borderSoft}`;
          }
        });
      });
    });

    this.element.querySelectorAll('.sav-form__error').forEach((errorEl) => {
      setStyles(errorEl, {
        color: error,
        fontSize: '0.875rem',
        marginTop: '0.5rem'
      });
    });

    this.element.querySelectorAll('.sav-radio-group').forEach((group) => {
      setStyles(group, {
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'stretch',
        width: '100%'
      });
    });

    this.element.querySelectorAll('.sav-radio').forEach((radio) => {
      setStyles(radio, {
        flex: '1 1 0',
        display: 'flex',
        width: '100%',
        maxWidth: 'none'
      });
    });

    const radioLabels = this.element.querySelectorAll('.sav-radio__label');
    radioLabels.forEach((label) => {
      setStyles(label, {
        border: `1.6px solid ${radioBorder}`,
        borderRadius: '8px',
        padding: '1rem',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '0.75rem',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        background: radioDefaultBackground,
        width: '100%',
        height: '100%',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'visible'
      });

      bindOnce(label, 'savRadioHoverBound', () => {
        label.addEventListener('mouseenter', () => {
          const input = label.closest('.sav-radio')?.querySelector('.sav-radio__input');
          if (!input || input.checked) return;
          label.style.backgroundColor = radioHoverBackground;
          label.style.borderColor = radioBorder;
        });

        label.addEventListener('mouseleave', () => {
          const input = label.closest('.sav-radio')?.querySelector('.sav-radio__input');
          if (!input) return;
          label.style.backgroundColor = input.checked ? radioHoverBackground : radioDefaultBackground;
          label.style.borderColor = input.checked ? primary : radioBorder;
        });
      });
    });

    this.element.querySelectorAll('.sav-radio__media').forEach((media) => {
      setStyles(media, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px'
      });
    });

    this.element.querySelectorAll('.sav-radio__media svg').forEach((icon) => {
      setStyles(icon, {
        width: '28px',
        height: '28px',
        display: 'block'
      });
    });

    this.element.querySelectorAll('.sav-radio__content').forEach((content) => {
      setStyles(content, {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.35rem',
        width: '100%'
      });
    });

    this.element.querySelectorAll('.sav-radio__title-row').forEach((row) => {
      setStyles(row, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        width: '100%'
      });
    });

    if (!isMobile && radioLabels.length > 1) {
      radioLabels.forEach((label) => {
        label.style.minHeight = '0';
      });
      const maxHeight = Math.max(...Array.from(radioLabels).map((label) => label.getBoundingClientRect().height));
      radioLabels.forEach((label) => {
        label.style.minHeight = `${Math.ceil(maxHeight)}px`;
      });
    } else {
      radioLabels.forEach((label) => {
        label.style.minHeight = '0';
      });
    }

    const radioInputs = this.element.querySelectorAll('.sav-radio__input');
    radioInputs.forEach((input) => {
      setStyles(input, {
        accentColor: primary,
        width: '18px',
        height: '18px',
        cursor: 'pointer'
      });

      bindOnce(input, 'savStyleBound', () => {
        input.addEventListener('change', () => {
          radioInputs.forEach((radio) => {
            const label = radio.closest('.sav-radio')?.querySelector('.sav-radio__label');
            if (!label) return;
            label.style.backgroundColor = radio.checked ? radioHoverBackground : radioDefaultBackground;
            label.style.borderColor = radio.checked ? primary : radioBorder;
          });
        });
      });
    });

    radioInputs.forEach((radio) => {
      const label = radio.closest('.sav-radio')?.querySelector('.sav-radio__label');
      if (!label) return;
      label.style.backgroundColor = radio.checked ? radioHoverBackground : radioDefaultBackground;
      label.style.borderColor = radio.checked ? primary : radioBorder;
    });

    this.element.querySelectorAll('.sav-radio__icon').forEach((icon) => {
      const isServiceReasonIcon = icon.closest('.sav-service-reason-group');
      setStyles(icon, {
        width: '24px',
        height: '24px',
        border: `1px solid ${border}`,
        borderRadius: isServiceReasonIcon ? '4px' : '50%',
        position: 'relative',
        transition: 'all 0.2s ease',
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-radio__title').forEach((title) => {
      setStyles(title, {
        display: 'flex',
        alignItems: 'center',
        gap: '0.42rem',
        fontWeight: '600',
        lineHeight: '1.3'
      });
    });

    this.element.querySelectorAll('.sav-products').forEach((list) => {
      setStyles(list, {
        display: 'grid',
        gap: '0.95rem'
      });
    });

    this.element.querySelectorAll('.sav-products__placeholder').forEach((placeholder) => {
      const placeholderColor = placeholder.style.color || colorTextA50;
      setStyles(placeholder, {
        textAlign: 'center',
        padding: '2rem',
        border: `1px dashed ${border}`,
        borderRadius: cardRadius,
        background: '#fff',
        color: placeholderColor
      });
    });

    this.element.querySelectorAll('.sav-product').forEach((card) => {
      const isSelected = card.classList.contains('is-selected');
      setStyles(card, {
        display: 'flex',
        gap: '1rem',
        padding: '1rem',
        border: `1px solid ${isSelected ? primary : borderSoft}`,
        borderRadius: cardRadius,
        cursor: 'pointer',
        transition: `border-color ${0.2 * motionFactor}s ease, background-color ${0.2 * motionFactor}s ease`,
        background: '#fff',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'visible'
      });

      bindOnce(card, 'savHoverBound', () => {
        card.addEventListener('mouseenter', () => {
          card.style.borderColor = primary;
        });
        card.addEventListener('mouseleave', () => {
          card.style.borderColor = card.classList.contains('is-selected') ? primary : borderSoft;
        });
      });
    });

    this.element.querySelectorAll('.sav-product__image').forEach((image) => {
      setStyles(image, {
        width: '90px',
        height: '90px',
        objectFit: 'cover',
        borderRadius: '0',
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-product__name').forEach((name) => {
      setStyles(name, {
        fontWeight: '600',
        marginBottom: '0.25rem'
      });
    });

    this.element.querySelectorAll('.sav-product__sku').forEach((sku) => {
      setStyles(sku, {
        fontSize: '0.875rem',
        color: colorTextA50
      });
    });

    this.element.querySelectorAll('.sav-faq').forEach((faq) => {
      setStyles(faq, {
        margin: '0 0 1.5rem',
        padding: '1.2rem 1.35rem 0.35rem',
        background: '#fff',
        border: `1px solid ${borderSoft}`,
        borderRadius: cardRadius,
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-faq__title').forEach((title) => {
      setStyles(title, {
        fontSize: '0.78rem',
        fontWeight: '500',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colorTextA80,
        margin: '0 0 0.95rem'
      });
    });

    this.element.querySelectorAll('.sav-faq__empty').forEach((empty) => {
      const emptyColor = empty.style.color || colorTextA80;
      setStyles(empty, {
        color: emptyColor,
        margin: '0 0 0.8rem',
        padding: '1rem 0',
        borderTop: `1px solid ${borderSoft}`,
        borderBottom: `1px solid ${borderSoft}`
      });
    });

    const faqItems = this.element.querySelectorAll('.sav-faq__item');
    faqItems.forEach((item, index) => {
      setStyles(item, {
        borderTop: `1px solid ${borderSoft}`,
        transition: 'border-color 0.2s ease'
      });
      if (index === faqItems.length - 1) {
        item.style.borderBottom = `1px solid ${borderSoft}`;
      }
    });

    this.element.querySelectorAll('.sav-faq__question').forEach((question) => {
      const isStatic = question.closest('.sav-faq')?.classList.contains('is-static');
      setStyles(question, {
        width: '100%',
        padding: isMobile ? '1rem 0' : '1.15rem 0',
        background: 'none',
        border: 'none',
        textAlign: 'left',
        fontSize: isMobile ? '1rem' : faqQuestionSize,
        fontWeight: '600',
        lineHeight: '1.4',
        letterSpacing: '0.01em',
        cursor: isStatic ? 'default' : 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        color: colorText,
        transition: `color ${0.25 * motionFactor}s ease`
      });
    });

    this.element.querySelectorAll('.sav-faq__icon').forEach((icon) => {
      const isStatic = icon.closest('.sav-faq')?.classList.contains('is-static');
      setStyles(icon, {
        display: isStatic ? 'none' : 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '26px',
        height: '26px',
        minWidth: '26px',
        fontSize: '0.94rem',
        lineHeight: '1',
        borderRadius: '0',
        border: '0',
        background: 'transparent',
        transition: `transform ${0.25 * motionFactor}s ease`
      });
    });

    this.element.querySelectorAll('.sav-faq__answer').forEach((answer) => {
      setStyles(answer, {
        color: colorTextA80,
        paddingRight: isMobile ? '0' : '2rem'
      });
    });

    this.element.querySelectorAll('.sav-faq__answer-text').forEach((text) => {
      setStyles(text, { margin: '0' });
    });

    this.element.querySelectorAll('.sav-faq__video').forEach((video) => {
      setStyles(video, {
        marginTop: '1rem',
        borderRadius: videoRadiusValue,
        overflow: 'hidden',
        background: '#000'
      });
    });

    this.element.querySelectorAll('.sav-faq__video iframe, .sav-faq__video video').forEach((media) => {
      setStyles(media, {
        display: 'block',
        width: '100%',
        aspectRatio: '16 / 9',
        border: '0'
      });
    });

    this.element.querySelectorAll('.sav-faq-actions').forEach((actions) => {
      if (actions.dataset.savHidden === 'true') {
        actions.style.display = 'none';
        return;
      }
      setStyles(actions, {
        marginTop: '1.6rem',
        paddingTop: '1.2rem',
        borderTop: `1px solid ${borderSoft}`,
        justifyContent: 'space-between'
      });
      styleButtonGroup(actions);
    });

    this.element.querySelectorAll('.sav-faq-actions .sav-btn').forEach((btn) => {
      setStyles(btn, { flex: '1 1 auto' });
    });

    this.element.querySelectorAll('.sav-tips-intro').forEach((intro) => {
      setStyles(intro, {
        margin: '0 0 1.35rem',
        color: colorTextA80,
        fontSize: '0.98rem',
        lineHeight: '1.68',
        maxWidth: '68ch',
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: intro.classList.contains('sav-tips-intro--left') ? 'left' : 'center'
      });
    });

    this.element.querySelectorAll('.sav-upload').forEach((upload) => {
      setStyles(upload, {
        border: `1px dashed ${border}`,
        borderRadius: cardRadius,
        padding: '2rem',
        textAlign: 'center',
        cursor: 'pointer',
        transition: `border-color ${0.2 * motionFactor}s ease`,
        background: '#fff',
        boxShadow: 'none'
      });

      bindOnce(upload, 'savHoverBound', () => {
        upload.addEventListener('mouseenter', () => {
          upload.style.borderColor = primary;
        });
        upload.addEventListener('mouseleave', () => {
          upload.style.borderColor = border;
        });
      });
    });

    this.element.querySelectorAll('.sav-upload__icon').forEach((icon) => {
      setStyles(icon, {
        width: '32px',
        height: '32px',
        margin: '0 auto 0.7rem',
        borderRadius: '0',
        border: '0',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        color: primary,
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-upload__text').forEach((text) => {
      setStyles(text, { color: colorTextA80 });
    });

    this.element.querySelectorAll('.sav-upload__limit').forEach((limit) => {
      setStyles(limit, {
        fontSize: '0.75rem',
        color: colorTextA50,
        marginTop: '0.5rem'
      });
    });

    this.element.querySelectorAll('.sav-previews').forEach((previews) => {
      setStyles(previews, {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '1rem',
        marginTop: '1rem'
      });
    });

    this.element.querySelectorAll('.sav-preview').forEach((preview) => {
      setStyles(preview, {
        position: 'relative',
        aspectRatio: '1',
        borderRadius: cardRadius,
        overflow: 'hidden'
      });
    });

    this.element.querySelectorAll('.sav-preview__image').forEach((image) => {
      setStyles(image, {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      });
    });

    this.element.querySelectorAll('.sav-preview__remove').forEach((btn) => {
      setStyles(btn, {
        position: 'absolute',
        top: '0.5rem',
        right: '0.5rem',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.9)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease'
      });

      bindOnce(btn, 'savHoverBound', () => {
        btn.addEventListener('mouseenter', () => {
          btn.style.background = error;
          btn.style.color = '#ffffff';
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.background = 'rgba(255, 255, 255, 0.9)';
          btn.style.color = '';
        });
      });
    });

    this.element.querySelectorAll('.sav-summary').forEach((summary) => {
      setStyles(summary, {
        background: '#fff',
        border: `1px solid ${borderSoft}`,
        borderRadius: cardRadius,
        padding: '2rem',
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-summary__section').forEach((section) => {
      setStyles(section, {
        marginBottom: '1.5rem',
        paddingBottom: '1.5rem',
        borderBottom: `1px solid ${borderSoft}`
      });
    });

    this.element.querySelectorAll('.sav-summary__section:last-child').forEach((section) => {
      setStyles(section, {
        marginBottom: '0',
        paddingBottom: '0',
        borderBottom: 'none'
      });
    });

    this.element.querySelectorAll('.sav-summary__label').forEach((label) => {
      setStyles(label, {
        fontSize: summaryLabelSize,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: colorTextA50,
        marginBottom: '0.35rem'
      });
    });

    this.element.querySelectorAll('.sav-summary__value').forEach((value) => {
      setStyles(value, {
        fontWeight: '500',
        lineHeight: '1.58'
      });
    });

    this.element.querySelectorAll('.sav-actions').forEach((actions) => {
      setStyles(actions, {
        marginTop: '0.5rem',
        justifyContent: 'space-between'
      });
      styleButtonGroup(actions);
    });

    // ─── FIX: skip buttons that are explicitly hidden ───────────────────────
    this.element.querySelectorAll('.sav-btn').forEach((btn) => {
      if (btn.hasAttribute('hidden')) return;
      const isResendBtn = btn.hasAttribute('data-resend-otp');
      const isPrevBtn = btn.hasAttribute('data-prev-btn');

      const existingPadding = btn.style.padding;
      setStyles(btn, {
        padding: existingPadding || (isResendBtn ? '0.6rem 1.35rem' : '0.95rem 1.6rem'),
        borderRadius: buttonRadius,
        fontWeight: buttonFontWeight,
        fontStyle: buttonFontStyle,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        border: isPrevBtn ? '1px solid #000000' : 'none',
        background: isResendBtn ? '#f1f1f1' : (isPrevBtn ? '#ffffff' : buttonColor),
        fontSize: buttonFontSize,
        letterSpacing: buttonLetterSpacing,
        textTransform: buttonUppercase,
        position: 'relative',
        overflow: 'visible',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: buttonFontStack,
        color: (isResendBtn || isPrevBtn) ? '#111111' : '#ffffff',
        width: isResendBtn ? 'auto' : '100%'
      });

      bindOnce(btn, 'savStyleBound', () => {
        btn.addEventListener('mouseenter', () => {
          btn.style.backgroundColor = isResendBtn ? '#e7e7e7' : (isPrevBtn ? '#f7f7f7' : buttonHover);
        });
        btn.addEventListener('mouseleave', () => {
          btn.style.backgroundColor = isResendBtn ? '#f1f1f1' : (isPrevBtn ? '#ffffff' : buttonColor);
        });
      });
    });
    // ────────────────────────────────────────────────────────────────────────

    this.element.querySelectorAll('.sav-btn--primary, .sav-btn--secondary').forEach((btn) => {
      if (btn.hasAttribute('hidden')) return;
      if (btn.hasAttribute('data-resend-otp')) return;
      if (btn.hasAttribute('data-prev-btn')) return;
      setStyles(btn, {
        background: buttonColor,
        color: '#ffffff'
      });
    });

    this.element.querySelectorAll('.sav-loading').forEach((loading) => {
      setStyles(loading, {
        textAlign: 'center',
        padding: '2.5rem 1rem'
      });
    });

    this.element.querySelectorAll('.sav-spinner').forEach((spinner) => {
      setStyles(spinner, {
        width: '40px',
        height: '40px',
        border: `2px solid ${borderSoft}`,
        borderTopColor: primary,
        borderRadius: '50%',
        margin: '0 auto 1rem',
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-success').forEach((successEl) => {
      setStyles(successEl, {
        textAlign: 'center',
        padding: '3rem 2rem',
        border: `1px solid ${borderSoft}`,
        borderRadius: cardRadius,
        background: '#fff',
        boxShadow: 'none'
      });
    });

    this.element.querySelectorAll('.sav-success__icon').forEach((icon) => {
      setStyles(icon, {
        width: '52px',
        height: '52px',
        borderRadius: '0',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        margin: '0 auto 1.5rem',
        boxShadow: 'none',
        color: success
      });
    });

    this.element.querySelectorAll('.sav-success__title').forEach((title) => {
      setStyles(title, {
        fontSize: '1.5rem',
        fontWeight: '600',
        marginBottom: '1rem',
        fontFamily: headingFontFamily,
        fontStyle: headingFontStyle
      });
    });

    this.element.querySelectorAll('.sav-success__reference').forEach((ref) => {
      setStyles(ref, {
        fontSize: '1rem',
        color: colorTextA80,
        marginBottom: '0.5rem'
      });
    });

    this.element.querySelectorAll('.sav-success__code').forEach((code) => {
      setStyles(code, {
        fontSize: '1.25rem',
        fontWeight: '600',
        color: primary,
        fontFamily: bodyFontFamily
      });
    });

    this.element.querySelectorAll('.sav-otp').forEach((otp) => {
      setStyles(otp, {
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
        margin: '2rem 0'
      });
    });

    this.element.querySelectorAll('.sav-otp__input').forEach((input) => {
      setStyles(input, {
        width: isMobile ? '40px' : '50px',
        height: isMobile ? '52px' : '60px',
        textAlign: 'center',
        fontSize: isMobile ? '1.2rem' : '1.5rem',
        fontWeight: '600',
        border: `1px solid ${border}`,
        borderRadius: inputRadius,
        background: '#fff',
        boxShadow: 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
      });

      bindOnce(input, 'savStyleBound', () => {
        input.addEventListener('focus', () => {
          input.style.borderColor = primary;
          input.style.boxShadow = '0 0 0 2px rgba(0, 0, 0, 0.08)';
        });
        input.addEventListener('blur', () => {
          input.style.borderColor = border;
          input.style.boxShadow = 'none';
        });
      });
    });

  }

  updateProgress() {
    const inactiveBorder = this.getStyleValue('--sav-lux-border', '#cfcfcf');
    const visibleSteps = this.getVisibleStepIndices();
    const currentVisiblePosition = visibleSteps.indexOf(this.currentStep);

    this.progressSteps.forEach((step, index) => {
      const circle = step.querySelector('.sav-progress__circle');
      const number = step.querySelector('.sav-progress__number');
      const label = step.querySelector('.sav-progress__label');
      const visiblePosition = visibleSteps.indexOf(index);
      const isHidden = visiblePosition === -1;

      step.hidden = isHidden;

      if (isHidden) {
        step.classList.remove('is-active', 'is-completed');
        return;
      }

      if (number) {
        number.textContent = String(visiblePosition + 1);
      }

      if (visiblePosition < currentVisiblePosition) {
        step.classList.add('is-completed');
        step.classList.remove('is-active');
        if (circle) {
          circle.style.backgroundColor = '#800000';
          circle.style.border = '1px solid #800000';
        }
        if (number) {
          number.style.color = '#ffffff';
        }
        if (label) {
          label.style.color = '#800000';
        }
      } else if (visiblePosition === currentVisiblePosition) {
        step.classList.add('is-active');
        step.classList.remove('is-completed');
        if (circle) {
          circle.style.backgroundColor = '#800000';
          circle.style.border = '2px solid #800000';
        }
        if (number) {
          number.style.color = '#ffffff';
        }
        if (label) {
          label.style.color = '#800000';
          label.style.fontWeight = '600';
        }
      } else {
        step.classList.remove('is-active', 'is-completed');
        if (circle) {
          circle.style.backgroundColor = '#e0e0e0';
          circle.style.border = `1px solid ${inactiveBorder}`;
        }
        if (number) {
          number.style.color = '#999999';
        }
        if (label) {
          label.style.color = '#999999';
        }
      }
    });

    if (this.progressLine) {
      const progressContainer = this.progressLine.parentElement;
      const firstVisibleStepIndex = visibleSteps[0];
      const firstCircle = this.progressSteps[firstVisibleStepIndex]?.querySelector('.sav-progress__circle');
      const activeCircle = this.progressSteps[this.currentStep]?.querySelector('.sav-progress__circle');

      if (progressContainer && firstCircle && activeCircle) {
        const containerRect = progressContainer.getBoundingClientRect();
        const firstRect = firstCircle.getBoundingClientRect();
        const activeRect = activeCircle.getBoundingClientRect();
        const start = (firstRect.left + (firstRect.width / 2)) - containerRect.left;
        const end = (activeRect.left + (activeRect.width / 2)) - containerRect.left;
        const width = Math.max(0, end - start);

        this.progressLine.style.left = `${start}px`;
        this.progressLine.style.right = 'auto';
        this.progressLine.style.width = `${width}px`;
      } else {
        const denominator = Math.max(visibleSteps.length - 1, 1);
        const progress = (Math.max(currentVisiblePosition, 0) / denominator) * 100;
        this.progressLine.style.left = '0';
        this.progressLine.style.right = 'auto';
        this.progressLine.style.width = `${progress}%`;
      }

      this.progressLine.style.backgroundColor = '#800000';
      this.progressLine.style.height = '2px';
      this.progressLine.style.transition = 'width 0.4s ease';
    }
  }

  updateButtons() {
    if (this.actions) {
      this.actions.style.display = 'flex';
    }

    if (this.faqActions) {
      this.faqActions.style.display = 'none';
      this.faqActions.dataset.savHidden = 'true';
    }

    // Precedent button
    if (this.prevBtn) {
      if (this.currentStep === 0) {
        this.prevBtn.style.display = 'none';
        this.prevBtn.setAttribute('hidden', '');
        this.prevBtn.setAttribute('aria-hidden', 'true');
        this.prevBtn.setAttribute('tabindex', '-1');
      } else {
        this.prevBtn.style.display = 'block';
        this.prevBtn.removeAttribute('hidden');
        this.prevBtn.removeAttribute('aria-hidden');
        this.prevBtn.removeAttribute('tabindex');
      }
    }

    // Next button text
    const buttonTexts = {
      0: this.formData.verified
        ? this.getMessage('buttonNext', 'Suivant')
        : (this.otpSection?.style.display === 'block'
            ? this.getMessage('buttonVerifyOtp', 'Vérifier le code')
            : this.getMessage('buttonVerify', 'Vérifier')),
      1: this.getMessage('buttonNext', 'Suivant'),
      3: this.shouldSkipReturnAddressStep()
        ? this.getMessage('buttonReview', 'Vérifier')
        : this.getMessage('buttonNext', 'Suivant'),
      4: this.getMessage('buttonReview', 'Vérifier'),
      5: this.getMessage('buttonSubmit', 'Envoyer la demande')
    };

    this.nextBtn.textContent = buttonTexts[this.currentStep] || this.getMessage('buttonNext', 'Suivant');
  }

  async validateStep(stepIndex) {
    switch(stepIndex) {
      case 0:
        return this.validateStep0();
      case 1:
        return this.validateStep1();
      case 2:
        return true;
      case 3:
        return this.validateStep2();
      case 4:
        return this.validateReturnAdresse();
      case 5:
        return true;
      default:
        return true;
    }
  }

  validateStep0() {
    this.clearErrors();

    const serviceType = this.getSelectedServiceType();
    this.formData.serviceType = serviceType;

    if (!serviceType) {
      this.showError('service-type', this.getMessage('serviceTypeRequired', 'Veuillez sélectionner le motif du service.'));
      return false;
    }

    if (this.requiresDetailedServiceReason(serviceType)) {
      const serviceReason = this.serviceReasonSelect ? this.serviceReasonSelect.value.trim() : '';
      if (!serviceReason) {
        this.showError('service-reason', this.getMessage('serviceReasonRequired', 'Veuillez sélectionner le motif de votre demande.'));
        return false;
      }

      this.formData.serviceReason = serviceReason;
    } else {
      this.formData.serviceReason = '';
    }

    if (this.formData.verified) {
      return true;
    }

    if (!this.formData.channel) {
      this.showError('purchase-channel', this.getMessage('purchaseChannelRequired', 'Veuillez choisir une option avant de continuer.'));
      return false;
    }

    if (this.formData.channel === 'online') {
      const orderNumber = this.orderNumberInput.value.trim();
      if (!orderNumber) {
        this.showError('order-number', this.getMessage('orderNumberRequired', 'Veuillez saisir votre numero de commande.'));
        return false;
      }
      this.formData.orderNumber = orderNumber;
    } else {
      const email = this.emailInput.value.trim();
      const boutiqueOrder = this.boutiqueOrderInput ? this.boutiqueOrderInput.value.trim() : '';
      const warrantyCard = this.warrantyCardInput.value.trim();

      if (!email) {
        this.showError('email', this.getMessage('emailRequired', 'Veuillez saisir votre adresse e-mail.'));
        return false;
      }

      if (!this.isValidEmail(email)) {
        this.showError('email', this.getMessage('emailInvalid', 'Veuillez saisir une adresse e-mail valide.'));
        return false;
      }

      if (!boutiqueOrder && !warrantyCard) {
        this.showError(
          'identifier',
          this.getMessage('identifierRequired', 'Renseignez soit le numero de commande boutique, soit le numero de carte de garantie.')
        );
        return false;
      }

      this.formData.email = email;
      this.formData.boutiqueOrder = boutiqueOrder;
      this.formData.warrantyCard = warrantyCard;
    }

    return true;
  }



  validateStep1() {
    const productError = this.element.querySelector('[data-error="product-selection"]');
    const exchangeError = this.element.querySelector('[data-error="exchange-selection"]');
    if (productError) {
      productError.textContent = '';
      productError.classList.remove('is-visible');
    }
    if (exchangeError) {
      exchangeError.textContent = '';
      exchangeError.classList.remove('is-visible');
    }

    if (this.availableProducts.length === 0) {
      let messageKey = 'noProductsConfigured';
      let fallbackMessage = "Aucune montre SAV n'est configurée. Ajoutez des blocs \"Produit SAV\" dans l'éditeur de thème.";
      if (this.productsLoadState === 'no-orders' || this.productsLoadState === 'no-matches') {
        messageKey = 'noProductsFoundInOrders';
        fallbackMessage = "Aucune montre trouvée dans vos commandes.";
      } else if (this.productsLoadState === 'error') {
        messageKey = 'loadProductsFailed';
        fallbackMessage = "Impossible de charger les montres.";
      }

      this.showError('product-selection', this.getMessage(messageKey, fallbackMessage));
      return false;
    }

    if (!this.formData.selectedProduct) {
      this.showError('product-selection', this.getMessage('noProductSelected', 'Veuillez selectionner votre montre.'));
      return false;
    }

    if (this.formData.serviceType === 'exchange') {
      if (this.exchangeCandidates.length === 0) {
        const messageKey = this.exchangeCandidatesLoadState === 'error'
          ? 'loadExchangeProductsFailed'
          : 'noExchangeProductsFound';
        const fallbackMessage = this.exchangeCandidatesLoadState === 'error'
          ? "Impossible de charger les produits d'échange."
          : "Aucun produit d'échange au même prix n'est disponible pour cette montre.";
        this.showError('exchange-selection', this.getMessage(messageKey, fallbackMessage));
        return false;
      }

      if (!this.formData.selectedExchangeProduct) {
        this.showError('exchange-selection', this.getMessage('exchangeProductRequired', "Veuillez sélectionner le produit d'échange souhaité."));
        return false;
      }
    }

    return true;
  }

  validateStep2() {
    this.clearErrors();
    let isValid = true;

    const problemCategory = this.problemCategorySelect.value;
    if (!problemCategory) {
      this.showError('problem-category', this.getMessage('problemCategoryRequired', 'Veuillez selectionner la categorie du probleme.'));
      isValid = false;
    }

    const problemDescription = this.problemDescriptionTextarea.value.trim();
    if (!problemDescription || problemDescription.length < 20) {
      this.showError(
        'problem-description',
        this.getMessage('problemDescriptionRequired', 'Decrivez le probleme en detail (au moins 20 caracteres).')
      );
      isValid = false;
    }

    if (isValid) {
      this.formData.problemCategory = problemCategory;
      this.formData.problemDescription = problemDescription;
    }

    return isValid;
  }

  validateReturnAdresse() {
    if (this.shouldSkipReturnAddressStep()) {
      this.clearReturnAddressData();
      return true;
    }

    this.clearErrors();
    let isValid = true;

    const getValue = (input) => (input ? input.value.trim() : '');

    const address1 = getValue(this.returnAddress1Input);
    const address2 = getValue(this.returnAddress2Input);
    const city = getValue(this.returnCityInput);
    const zip = getValue(this.returnZipInput);
    const province = getValue(this.returnProvinceInput);
    const country = getValue(this.returnCountryInput);
    const phone = getValue(this.returnPhoneInput);

    if (!address1) {
      this.showError('return-address-1', this.getMessage('returnAddress1Required', "Veuillez saisir l'adresse (ligne 1)."));
      isValid = false;
    }

    if (!city) {
      this.showError('return-city', this.getMessage('returnCityRequired', 'Veuillez saisir la ville.'));
      isValid = false;
    }

    if (!zip) {
      this.showError('return-zip', this.getMessage('returnZipRequired', 'Veuillez saisir le code postal.'));
      isValid = false;
    }

    if (!province) {
      this.showError('return-province', this.getMessage('returnProvinceRequired', "Veuillez saisir la région / l'état."));
      isValid = false;
    }

    if (!country) {
      this.showError('return-country', this.getMessage('returnCountryRequired', 'Veuillez saisir le pays.'));
      isValid = false;
    }

    if (isValid) {
      this.formData.returnAddress1 = address1;
      this.formData.returnAddress2 = address2;
      this.formData.returnStreet = address1;
      this.formData.returnCity = city;
      this.formData.returnZip = zip;
      this.formData.returnProvince = province;
      this.formData.returnCountry = country;
      this.formData.returnPhone = phone;
      this.formData.returnPhoneCountry = getValue(this.returnPhoneCountryInput);
    }

    return isValid;
  }

  async handleVerification() {

      try {
        this.setLoading(true);
        const verificationPayload = {
          form_type: this.formData.channel === 'online' ? 'online' : 'store',
          sav_online_order_number: this.formData.channel === 'online'
            ? this.formData.orderNumber
            : (this.formData.warrantyCard || this.formData.boutiqueOrder),
          sav_online_email: this.formData.email,
          locale: this.getStorefrontLocale(),
        };
        this.logApiRequest('verify-order', verificationPayload);
        const response = await fetch(`${this.API_BASE_URL}/sav-order/verify-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(verificationPayload)
        });

        const data = await response.json().catch(() => ({}));
        this.logApiResponse('verify-order', response, data);
        const isSuccess = response.ok && this.isApiSuccess(data.success);

        if (!isSuccess) {
          const fallbackMessage = this.formData.channel === 'online'
            ? this.getMessage('orderNotFound', 'Commande introuvable. Vérifiez le numéro saisi.')
            : this.getMessage('verificationFailed', 'La vérification a échoué. Veuillez réessayer.');
          const apiMessage = typeof data.message === 'string' ? data.message.trim() : '';
          const targetField = this.formData.channel === 'online' ? 'order-number' : 'identifier';
          const finalMessage = this.resolveVerificationErrorMessage(apiMessage, fallbackMessage, response.status);
          this.showError(targetField, finalMessage);
          return false;
        }

        this.userEmail = data.email || this.formData.email || '';
        this.formData.email = this.userEmail || this.formData.email || '';
        this.orderName = data.order_name || this.formData.orderNumber || this.formData.boutiqueOrder || '';
        this.formData.locale = data.locale || this.getStorefrontLocale();

        this.otpSection.style.display = 'block';
        this.updateButtons();
        this.requestVisualRefresh();

        // Focus first OTP input
        if (this.otpInputs && this.otpInputs[0]) {
          this.otpInputs[0].focus();
        }
        return true;

      } catch(error) {
          console.error('Verification error:', error);
          const fallbackMessage = this.formData.channel === 'online'
            ? this.getMessage('orderNotFound', 'Commande introuvable. Vérifiez le numéro saisi.')
            : this.getMessage('verificationFailed', 'La vérification a échoué. Veuillez réessayer.');
          const targetField = this.formData.channel === 'online' ? 'order-number' : 'identifier';
          this.showError(
            targetField,
            this.resolveVerificationErrorMessage(error.message, fallbackMessage)
          );
          return false;
      } finally {
        this.setLoading(false)
      }
  }

async verifyOtp() {
  const otp = Array.from(this.otpInputs).map(input => input.value).join('');

  if (otp.length !== 6) {
    this.showError('otp', this.getMessage('otpIncomplete', 'Veuillez saisir le code complet a 6 chiffres.'));
    return false;
  }

  try {
    this.setLoading(true);
    const otpPayload = {
      sav_online_email: this.userEmail,
      otp_code: otp,
    };
    this.logApiRequest('verify-otp', otpPayload);

    const response = await fetch(`${this.API_BASE_URL}/sav-order/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(otpPayload)
    });

    const data = await response.json().catch(() => ({}));
    this.logApiResponse('verify-otp', response, data);
    const isSuccess = response.ok && this.isApiSuccess(data.success);

    if (!isSuccess) {
      const fallbackMessage = this.getMessage('otpInvalid', 'Code de vérification invalide.');
      const apiMessage = typeof data.message === 'string' ? data.message.trim() : '';
      this.showError('otp', this.normalizeErrorCasing(apiMessage) || fallbackMessage);
      return false;

    }


    this.formData.verified = true;
    this.formData.customerData = data.customerData;
    await this.loadProducts();
    this.currentStep++;
    this.showStep(this.currentStep);

    return true;

  } catch (error) {
    console.error('❌ OTP verification error:', error);
    const rawMessage = typeof error?.message === 'string' ? error.message : '';
    this.showError('otp', this.normalizeErrorCasing(rawMessage) || this.getMessage('otpInvalid', 'Code de verification invalide.'));
    return false;
  } finally {
    this.setLoading(false);
  }
}

  handleOtpInput(e, index) {
    const input = e.target;
    const value = input.value;

    // Only allow digits
    if (!/^\d*$/.test(value)) {
      input.value = '';
      return;
    }

    // Move to next input
    if (value && index < this.otpInputs.length - 1) {
      this.otpInputs[index + 1].focus();
    }

    // Check if all inputs are filled
    const allFilled = Array.from(this.otpInputs).every(input => input.value);
    if (allFilled) {
      this.verifyOtp();
    }
  }

  handleOtpKeydown(e, index) {
    // Handle backspace
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      this.otpInputs[index - 1].focus();
    }
  }

  handleOtpPaste(e) {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    pastedData.split('').forEach((char, index) => {
      if (this.otpInputs[index]) {
        this.otpInputs[index].value = char;
      }
    });

    // Focus last filled input
    const lastIndex = Math.min(pastedData.length, this.otpInputs.length - 1);
    this.otpInputs[lastIndex].focus();

    // Auto-verify if all filled
    if (pastedData.length === 6) {
      this.verifyOtp();
    }
  }

  async resendOtp() {
    try {
      this.clearFeedback('otpResend');
      this.setLoading(true);
      // Renvoyer un code = ré-émettre une vérification : le backend regénère
      // un OTP (indexé par e-mail client) et le renvoie par e-mail.
      const resendPayload = {
        form_type: this.formData.channel === 'online' ? 'online' : 'store',
        sav_online_order_number: this.formData.channel === 'online'
          ? this.formData.orderNumber
          : (this.formData.warrantyCard || this.formData.boutiqueOrder),
        sav_online_email: this.formData.email,
        locale: this.getStorefrontLocale(),
      };
      this.logApiRequest('resend-otp', resendPayload);

      const response = await fetch(`${this.API_BASE_URL}/sav-order/verify-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(resendPayload)
      });
      const data = await response.json().catch(() => ({}));
      this.logApiResponse('resend-otp', response, data);

      if (!response.ok || !this.isApiSuccess(data.success)) {
        throw new Error(this.getMessage('resendCodeFailed', 'Impossible de renvoyer le code. Veuillez reessayer.'));
      }

      this.showNotification(
        this.getMessage('resendCodeSuccess', 'Un nouveau code de verification a ete envoye.'),
        'success',
        { target: 'otpResend' }
      );

      // Clear OTP inputs
      this.otpInputs.forEach(input => input.value = '');
      this.otpInputs[0].focus();

    } catch (error) {
      console.error('Resend OTP error:', error);
      this.showNotification(
        error.message || this.getMessage('resendCodeFailed', 'Impossible de renvoyer le code. Veuillez reessayer.'),
        'error',
        { target: 'otpResend' }
      );
    } finally {
      this.setLoading(false);
    }
  }

async loadProducts() {
  if (!this.productsList) {
    return;
  }

  try {
    if (this.faqSection) {
      this.faqSection.style.display = 'none';
      this.faqSection.classList.remove('is-static');
    }
    if (this.faqItems) {
      this.faqItems.innerHTML = '';
    }

    this.formData.selectedProduct = null;
    this.clearExchangeSelection({ resetMarkup: true });
    this.availableProducts = [];
    this.productsLoadState = 'loading';
    this.syncExchangeProductsVisibility();

    const orderName = (this.orderName || this.formData.orderNumber || this.formData.boutiqueOrder || '').trim();
    const loadProductsPayload = {
      order_name: orderName,
      action: 'load_products',
      serial_number: this.formData.warrantyCard || this.formData.boutiqueOrder || ''
    };
    this.logApiRequest('load-products', loadProductsPayload);

    const response = await fetch(`${this.API_BASE_URL}/sav-order/loadProducts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(loadProductsPayload)
    });

    const data = await response.json();
    this.logApiResponse('load-products', response, data);

    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      if (value && (typeof value === 'object' || typeof value === 'string' || typeof value === 'number')) {
        return [value];
      }
      return [];
    };

    const extractItems = (source) => {
      if (!source) return [];
      const candidates = [
        source.products,
        source.items,
        source.line_items,
        source.lineItems,
        source.order?.line_items,
        source.order?.items,
        source.order?.products,
        source.product,
        source.item
      ];
      for (const candidate of candidates) {
        const normalized = toArray(candidate).filter(Boolean);
        if (normalized.length) return normalized;
      }
      return [];
    };

    let purchasedItems;

    if (Array.isArray(data)) {
      purchasedItems = data;
    } else {
      purchasedItems = extractItems(data);
      if (purchasedItems.length === 0 && Array.isArray(data?.orders)) {
        purchasedItems = data.orders.flatMap((order) => extractItems(order));
      }
    }

    const purchasedSkus = new Set();
    const skuToOrderId = new Map();
    const skuToProductMeta = new Map();
    const orderIdCandidates = [];

    const addSku = (value) => {
      const normalized = this.normalizeSku(value);
      if (normalized) {
        purchasedSkus.add(normalized);
      }
    };

    const addOrderId = (value) => {
      if (value == null || value === '') return;
      orderIdCandidates.push(String(value));
    };

    purchasedItems.forEach((item) => {
      if (!item) return;
      if (typeof item === 'string' || typeof item === 'number') {
        addSku(item);
        return;
      }
      const itemOrderId = item.order_id ?? item.orderId ?? item.order?.id ?? item.order?.order_id ?? item.order?.orderId ?? null;
      addOrderId(itemOrderId);
      addSku(item.sku);
      addSku(item.SKU);
      addSku(item.variant_sku);
      addSku(item.variantSku);
      addSku(item.product_sku);
      addSku(item.productSku);
      addSku(item.sku_code);
      addSku(item.skuCode);
      addSku(item.variant?.sku);
      addSku(item.variant?.SKU);
      addSku(item.product?.sku);
      addSku(item.product?.SKU);

      const skuCandidates = [
        item.sku,
        item.SKU,
        item.variant_sku,
        item.variantSku,
        item.product_sku,
        item.productSku,
        item.sku_code,
        item.skuCode,
        item.variant?.sku,
        item.variant?.SKU,
        item.product?.sku,
        item.product?.SKU
      ];
      const normalizedSku = this.normalizeSku(skuCandidates.find((value) => value != null && String(value).trim()));
      if (normalizedSku && itemOrderId && !skuToOrderId.has(normalizedSku)) {
        skuToOrderId.set(normalizedSku, String(itemOrderId));
      }
      if (normalizedSku && typeof item === 'object' && !skuToProductMeta.has(normalizedSku)) {
        skuToProductMeta.set(normalizedSku, item);
      }
    });

    addOrderId(data?.order_id);
    addOrderId(data?.orderId);
    addOrderId(data?.order?.id);
    addOrderId(data?.order?.order_id);
    addOrderId(data?.order?.orderId);

    const fallbackOrderId = orderIdCandidates.find((value) => value != null && String(value).trim()) || '';
    this.formData.orderId = fallbackOrderId;

    const readFirstText = (values) => {
      for (const value of values) {
        if (value == null) continue;
        const normalized = String(value).trim();
        if (normalized) return normalized;
      }
      return '';
    };
    const backendProducts = purchasedItems
      .filter((item) => item && typeof item === 'object')
      .map((item, index) => {
        const sku = readFirstText([
          item.sku,
          item.SKU,
          item.variant_sku,
          item.variantSku,
          item.product_sku,
          item.productSku,
          item.variant?.sku,
          item.variant?.SKU
        ]);
        const image = readFirstText([
          item.image,
          item.imageLink,
          item.image_url,
          item.imageUrl,
          item.product?.image,
          item.product?.imageUrl
        ]);
        const collections = Array.isArray(item.collections)
          ? item.collections.map((handle) => String(handle)).filter(Boolean)
          : [];

        return {
          id: item.shopify_product_gid || item.shopifyProductGid || item.product_id || item.productId || item.id || `order-line-${index}`,
          name: readFirstText([
            item.name,
            item.Name,
            item.title,
            item.productTitle,
            item.product?.title,
            sku,
            `Produit ${index + 1}`
          ]),
          sku: sku || '',
          image,
          collections,
          imageLink: image,
          catalogItemId: item.item_id || item.itemId || '',
          orderLineItemId: item.order_line_item_id || item.orderLineItemId || '',
          sourceLineId: item.source_line_id || item.sourceLineId || '',
          priceAmount: item.price_amount || item.priceAmount || null,
          currencyCode: item.currency_code || item.currencyCode || '',
          shopifyProductGid: item.shopify_product_gid || item.shopifyProductGid || '',
          shopifyVariantGid: item.shopify_variant_gid || item.shopifyVariantGid || '',
          productSource: item.product_source || item.productSource || item.source || '',
          orderId: item.order_id || item.orderId || fallbackOrderId || ''
        };
      })
      .filter((product) => product.sku || product.name);

    if (backendProducts.length === 0) {
      this.productsLoadState = 'no-orders';
      this.productsList.innerHTML = `
        <p class="sav-products__placeholder">${this.escapeHtml(this.getMessage('noProductsFoundInOrders', 'Aucune montre trouvée dans vos commandes.'))}</p>
      `;
      this.requestVisualRefresh();
      return;
    }

    this.productsLoadState = 'ready';
    this.availableProducts = backendProducts;

    const fallbackSvg = "<svg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150' aria-hidden='true'><rect width='150' height='150' fill='%23cccccc'/><rect x='28' y='32' width='94' height='86' fill='none' stroke='%23666' stroke-width='4'/><circle cx='58' cy='62' r='9' fill='none' stroke='%23666' stroke-width='4'/><path d='M40 103l24-24 16 14 16-19 16 29z' fill='none' stroke='%23666' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'/></svg>";
    const fallbackImage = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(fallbackSvg)}`;

    this.productsList.innerHTML = this.availableProducts.map((product, index) => {
      const productImage = product.image || product.imageLink || fallbackImage;
      const skuLine = product.sku
        ? `<div class="sav-product__sku">SKU: ${this.escapeHtml(product.sku)}</div>`
        : '';

      return `
        <div class="sav-product" data-product-index="${index}">
          <img src="${this.escapeHtml(productImage)}" alt="${this.escapeHtml(product.name)}" class="sav-product__image" data-fallback-src="${this.escapeHtml(fallbackImage)}">
          <div class="sav-product__info">
            <div class="sav-product__name">${this.escapeHtml(product.name)}</div>
            ${skuLine}
          </div>
        </div>
      `;
    }).join('');

    this.bindProductImageFallbacks();

    this.productsList.querySelectorAll('.sav-product').forEach((productEl) => {
      productEl.addEventListener('click', () => this.selectProduct(productEl));
    });

    this.staggerIn(this.productsList.querySelectorAll('.sav-product'), 30, 30);
    this.syncExchangeProductsVisibility();
    this.requestVisualRefresh();

  } catch (error) {
    console.error('Load products error:', error);
    this.productsLoadState = 'error';
    this.productsList.innerHTML = `
      <p class="sav-products__placeholder" style="color: var(--sav-error);">
        ${this.escapeHtml(this.getMessage('loadProductsFailed', 'Impossible de charger les montres.'))}
      </p>
    `;
    this.requestVisualRefresh();
  }
}

  bindProductImageFallbacks() {
    if (!this.productsList) {
      return;
    }

    this.productsList.querySelectorAll('.sav-product__image[data-fallback-src]').forEach((img) => {
      if (img.dataset.fallbackBound === 'true') {
        return;
      }

      const applyFallback = () => {
        if (img.dataset.fallbackApplied === 'true') {
          return;
        }

        const fallbackSrc = img.dataset.fallbackSrc;
        if (!fallbackSrc) {
          return;
        }

        img.dataset.fallbackApplied = 'true';
        img.src = fallbackSrc;
      };

      img.dataset.fallbackBound = 'true';
      img.addEventListener('error', applyFallback);

      if (img.complete && img.naturalWidth === 0) {
        applyFallback();
      }
    });
  }

  selectProduct(productEl) {
    const borderSoft = this.getStyleValue('--sav-lux-border-soft', '#dfdfdf');
    const primary = '#800000';
    // Remove previous selection
    this.productsList.querySelectorAll('.sav-product').forEach(el => {
      el.classList.remove('is-selected');
      el.style.borderColor = borderSoft;
    });

    // Select this product
    productEl.classList.add('is-selected');
    productEl.style.borderColor = primary;
    const productIndex = Number(productEl.dataset.productIndex);
    this.formData.selectedProduct = this.availableProducts[productIndex] || null;
    this.formData.orderId = this.formData.selectedProduct?.orderId || this.formData.orderId || '';

    const productError = this.element.querySelector('[data-error="product-selection"]');
    if (productError) {
      productError.textContent = '';
      productError.classList.remove('is-visible');
    }

    this.clearExchangeSelection({ resetMarkup: true });
    this.syncExchangeProductsVisibility();

    if (this.formData.serviceType === 'exchange') {
      this.loadExchangeCandidates();
    }

    // La disponibilité des conseils dépend du produit choisi : rafraîchit la barre d'étapes
    this.updateProgress();
  }

  async loadExchangeCandidates() {
    if (!this.exchangeProductsList) {
      return;
    }

    if (this.formData.serviceType !== 'exchange' || !this.formData.selectedProduct) {
      this.clearExchangeSelection({ resetMarkup: true });
      this.syncExchangeProductsVisibility();
      return;
    }

    this.syncExchangeProductsVisibility();
    this.exchangeCandidates = [];
    this.exchangeCandidatesLoadState = 'loading';
    this.formData.selectedExchangeProduct = null;
    this.exchangeProductsList.innerHTML = `
      <p class="sav-products__placeholder">${this.escapeHtml(this.getMessage('exchangeProductsLoading', "Recherche des produits d'échange disponibles..."))}</p>
    `;
    this.requestVisualRefresh();

    try {
      const orderName = (this.orderName || this.formData.orderNumber || this.formData.boutiqueOrder || '').trim();
      const loadExchangeCandidatesPayload = {
        order_name: orderName,
        serial_number: this.formData.warrantyCard || this.formData.boutiqueOrder || '',
        sku: this.formData.selectedProduct?.sku || '',
        item_id: this.formData.selectedProduct?.catalogItemId || this.formData.selectedProduct?.itemId || ''
      };
      this.logApiRequest('load-exchange-candidates', loadExchangeCandidatesPayload);
      const response = await fetch(`${this.API_BASE_URL}/sav-order/loadExchangeCandidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(loadExchangeCandidatesPayload)
      });

      const data = await response.json().catch(() => []);
      this.logApiResponse('load-exchange-candidates', response, data);
      const rawCandidates = Array.isArray(data)
        ? data
        : Array.isArray(data?.products)
          ? data.products
          : [];

      this.exchangeCandidates = rawCandidates
        .filter((candidate) => candidate && typeof candidate === 'object')
        .map((candidate) => ({
          ...candidate,
          name: candidate.name || candidate.Name || candidate.productTitle || candidate.label || candidate.sku || candidate.Sku || '',
          sku: candidate.sku || candidate.Sku || '',
          priceAmount: candidate.priceAmount || candidate.price_amount || null,
          currencyCode: candidate.currencyCode || candidate.currency_code || ''
        }))
        .filter((candidate) => candidate.name || candidate.sku);

      if (this.exchangeCandidates.length === 0) {
        this.exchangeCandidatesLoadState = 'no-matches';
        this.exchangeProductsList.innerHTML = `
          <p class="sav-products__placeholder">${this.escapeHtml(this.getMessage('noExchangeProductsFound', "Aucun produit d'échange au même prix n'est disponible pour cette montre."))}</p>
        `;
        this.requestVisualRefresh();
        return;
      }

      this.exchangeCandidatesLoadState = 'ready';
      this.exchangeProductsList.innerHTML = this.exchangeCandidates.map((product, index) => {
        const priceLine = product.priceAmount
          ? `<div class="sav-product__sku">${this.escapeHtml(String(product.priceAmount))}${product.currencyCode ? ` ${this.escapeHtml(product.currencyCode)}` : ''}</div>`
          : '';

        return `
          <div class="sav-product" data-exchange-product-index="${index}">
            <div class="sav-product__info">
              <div class="sav-product__name">${this.escapeHtml(product.name)}</div>
              ${product.sku ? `<div class="sav-product__sku">SKU: ${this.escapeHtml(product.sku)}</div>` : ''}
              ${priceLine}
            </div>
          </div>
        `;
      }).join('');

      this.exchangeProductsList.querySelectorAll('[data-exchange-product-index]').forEach((productEl) => {
        productEl.addEventListener('click', () => this.selectExchangeProduct(productEl));
      });

      this.staggerIn(this.exchangeProductsList.querySelectorAll('.sav-product'), 30, 30);
      this.requestVisualRefresh();
    } catch (error) {
      console.error('Load exchange candidates error:', error);
      this.exchangeCandidates = [];
      this.exchangeCandidatesLoadState = 'error';
      this.exchangeProductsList.innerHTML = `
        <p class="sav-products__placeholder" style="color: var(--sav-error);">
          ${this.escapeHtml(this.getMessage('loadExchangeProductsFailed', "Impossible de charger les produits d'échange."))}
        </p>
      `;
      this.requestVisualRefresh();
    }
  }

  selectExchangeProduct(productEl) {
    const borderSoft = this.getStyleValue('--sav-lux-border-soft', '#dfdfdf');
    const primary = '#800000';

    this.exchangeProductsList.querySelectorAll('.sav-product').forEach((el) => {
      el.classList.remove('is-selected');
      el.style.borderColor = borderSoft;
    });

    productEl.classList.add('is-selected');
    productEl.style.borderColor = primary;
    const productIndex = Number(productEl.dataset.exchangeProductIndex);
    this.formData.selectedExchangeProduct = this.exchangeCandidates[productIndex] || null;

    const exchangeError = this.element.querySelector('[data-error="exchange-selection"]');
    if (exchangeError) {
      exchangeError.textContent = '';
      exchangeError.classList.remove('is-visible');
    }
  }

  async loadFaq() {
    const selectedSku = this.normalizeSku(this.formData.selectedProduct?.sku);
    if (!selectedSku && !this.formData.selectedProduct?.id) return;

    const sectionCollapsible = this.faqSection?.dataset.faqCollapsible !== 'false';
    this.faqSection.style.display = 'block';
    this.faqSection.classList.toggle('is-static', !sectionCollapsible);

    const themeFaqItems = this.getThemeFaqItemsForSelectedProduct();
    if (themeFaqItems.length > 0) {
      this.renderFaqItems(themeFaqItems, sectionCollapsible);
      return;
    }

    // FAQ content comes exclusively from the section's "FAQ SAV" blocks — there is no remote FAQ source.
    this.faqItems.innerHTML = `<p class="sav-faq__empty">${this.escapeHtml(this.getMessage('noFaqConfigured', "Aucune FAQ n'est configurée pour la montre sélectionnée."))}</p>`;
    this.requestVisualRefresh();
  }

  getThemeFaqItemsForSelectedProduct() {
    const selectedCollections = Array.isArray(this.formData.selectedProduct?.collections)
      ? this.formData.selectedProduct.collections
          .map((handle) => String(handle).trim().toLowerCase())
          .filter(Boolean)
      : [];

    return this.themeConfig.faqItems.filter((item) => {
      if (item.applyToAll) return true;
      if (!item.collectionHandle) return false;
      return selectedCollections.includes(item.collectionHandle);
    });
  }

  renderFaqItems(items, sectionCollapsible) {
    if (!Array.isArray(items) || items.length === 0) {
      this.faqItems.innerHTML = `<p class="sav-faq__empty">${this.escapeHtml(this.getMessage('noFaqConfigured', "Aucune FAQ n'est configurée pour la montre sélectionnée."))}</p>`;
      this.requestVisualRefresh();
      return;
    }

    this.faqItems.innerHTML = items.map((item, index) => {
      const itemCollapsible = sectionCollapsible && item.isCollapsible !== false;
      const itemClass = itemCollapsible ? 'sav-faq__item' : 'sav-faq__item is-open';
      const questionClass = itemCollapsible ? 'sav-faq__question' : 'sav-faq__question is-static';
      const questionAttrs = itemCollapsible
        ? 'data-faq-toggle="true"'
        : 'aria-disabled="true" tabindex="-1" style="cursor: default;"';

      return `
        <div class="${itemClass}" data-faq-item="${index}">
          <button type="button" class="${questionClass}" ${questionAttrs}>
            <span>${this.escapeHtml(item.question || '')}</span>
            ${itemCollapsible ? '<span class="sav-faq__icon">▾</span>' : ''}
          </button>
          <div class="sav-faq__answer">
            <p class="sav-faq__answer-text">${this.formatMultilineText(item.answer || '')}</p>
            ${this.renderFaqVideo(item)}
          </div>
        </div>
      `;
    }).join('');

    this.faqItems.querySelectorAll('[data-faq-toggle="true"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.sav-faq__item');
        if (item) {
          item.classList.toggle('is-open');
        }
      });
    });

    this.staggerIn(this.faqItems.querySelectorAll('.sav-faq__item'), 30, 30);
    this.requestVisualRefresh();
  }

  renderFaqVideo(item) {
    const hostedVideoUrl = item.hostedVideoUrl || item.videoUrl || '';
    const hostedVideoPoster = item.hostedVideoPoster || item.videoPoster || '';
    if (hostedVideoUrl) {
      const poster = hostedVideoPoster
        ? ` poster="${this.escapeHtml(hostedVideoPoster)}"`
        : '';

      return `
        <div class="sav-faq__video">
          <video controls preload="metadata"${poster}>
            <source src="${this.escapeHtml(hostedVideoUrl)}" type="video/mp4">
          </video>
        </div>
      `;
    }

    const videoType = item.externalVideoType || item.videoType || '';
    const videoId = item.externalVideoId || item.videoId || '';
    const embedUrl = this.getExternalVideoEmbedUrl(videoType, videoId);
    if (!embedUrl) {
      return '';
    }

    return `
      <div class="sav-faq__video">
        <iframe
          src="${this.escapeHtml(embedUrl)}"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          title="Video FAQ"
        ></iframe>
      </div>
    `;
  }

  getExternalVideoEmbedUrl(videoType, videoId) {
    if (!videoType || !videoId) {
      return '';
    }

    const safeId = encodeURIComponent(String(videoId));
    if (videoType === 'youtube') {
      return `https://www.youtube.com/embed/${safeId}`;
    }
    if (videoType === 'vimeo') {
      return `https://player.vimeo.com/video/${safeId}`;
    }

    return '';
  }

  handlePhotoSelect(e) {
    const files = Array.from(e.target.files);

    // Validate file count
    if (this.formData.photos.length + files.length > 5) {
      this.showError('photos', this.getMessage('maxPhotosAllowed', "Vous pouvez ajouter jusqu'à 5 photos."));
      return;
    }

    // Validate each file
    for (const file of files) {
      // Check file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.showError('photos', this.getMessage('invalidPhotoType', 'Formats autorises : JPG, PNG et WEBP.'));
        return;
      }

      // Check file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.showError('photos', this.getMessage('invalidPhotoSize', 'Chaque fichier doit faire moins de 10 Mo.'));
        return;
      }
    }

    // Add files to formData
    files.forEach(file => {
      this.formData.photos.push(file);
      this.addPhotoPreview(file);
    });

    e.target.value = '';
  }

  addPhotoPreview(file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const preview = document.createElement('div');
      preview.className = 'sav-preview';
      preview.innerHTML = `
        <img src="${e.target.result}" alt="Preview" class="sav-preview__image">
        <button type="button" class="sav-preview__remove" data-file-name="${file.name}">×</button>
      `;

      // Bind remove event
      preview.querySelector('.sav-preview__remove').addEventListener('click', () => {
        this.removePhoto(file.name, preview);
      });

      this.photoPreviews.appendChild(preview);
      this.requestVisualRefresh();
    };

    reader.readAsDataURL(file);
  }

  removePhoto(fileName, previewEl) {
    // Remove from formData
    this.formData.photos = this.formData.photos.filter(file => file.name !== fileName);

    // Remove preview
    previewEl.remove();
  }

  populateSummary() {
    this.summaryChannel.textContent =
      this.formData.channel === 'online'
        ? this.getMessage('summaryOnlinePurchase', 'Achat en ligne')
        : this.formData.channel === 'boutique'
          ? this.getMessage('summaryBoutiquePurchase', 'Achat en boutique')
          : '';
    if (this.summaryServiceType) {
      this.summaryServiceType.textContent = this.getServiceTypeLabel();
    }
    if (this.summaryServiceReasonSection) {
      this.summaryServiceReasonSection.hidden = !this.formData.serviceReason;
    }
    if (this.summaryServiceReason) {
      this.summaryServiceReason.textContent = this.getServiceReasonLabel();
    }
    const summaryEmail = this.userEmail || this.formData.email || '';
    this.summaryEmail.textContent = summaryEmail ? this.maskEmail(summaryEmail) : '';
    const selectedProduct = this.formData.selectedProduct || {};
    this.summaryWatch.textContent = selectedProduct.sku
      ? `${selectedProduct.name} (${selectedProduct.sku})`
      : (selectedProduct.name || '');
    if (this.summaryExchangeProductSection) {
      this.summaryExchangeProductSection.hidden = !(
        this.formData.serviceType === 'exchange' && this.formData.selectedExchangeProduct
      );
    }
    if (this.summaryExchangeProduct) {
      const exchangeProduct = this.formData.selectedExchangeProduct || {};
      this.summaryExchangeProduct.textContent = exchangeProduct.sku
        ? `${exchangeProduct.name} (${exchangeProduct.sku})`
        : (exchangeProduct.name || '');
    }
    const shouldHideProblemDetailsStep = this.shouldSkipProblemDetailsStep();
    const summaryCategorySection = this.summaryCategory?.closest('.sav-summary__section');
    if (summaryCategorySection) {
      summaryCategorySection.hidden = shouldHideProblemDetailsStep;
    }
    if (this.summaryCategory) {
      this.summaryCategory.textContent = shouldHideProblemDetailsStep
        ? ''
        : this.problemCategorySelect.options[this.problemCategorySelect.selectedIndex].text;
    }
    const summaryDescriptionSection = this.summaryDescription?.closest('.sav-summary__section');
    if (summaryDescriptionSection) {
      summaryDescriptionSection.hidden = shouldHideProblemDetailsStep;
    }
    if (this.summaryDescription) {
      this.summaryDescription.textContent = shouldHideProblemDetailsStep
        ? ''
        : this.formData.problemDescription;
    }
    if (this.summaryReturnAddressSection) {
      this.summaryReturnAddressSection.hidden = this.shouldSkipReturnAddressStep();
    }
    if (this.summaryReturnAddress) {
      const addressLines = [];
      if (this.formData.returnAddress1) addressLines.push(this.formData.returnAddress1);
      if (this.formData.returnAddress2) addressLines.push(this.formData.returnAddress2);
      const cityLineParts = [
        this.formData.returnCity,
        this.formData.returnProvince,
        this.formData.returnZip
      ].filter(Boolean);
      if (cityLineParts.length) addressLines.push(cityLineParts.join(', '));
      if (this.formData.returnCountry) addressLines.push(this.formData.returnCountry);
      this.summaryReturnAddress.innerHTML = this.formatMultilineText(addressLines.join('\n'));
    }
    const summaryPhotosSection = this.summaryPhotos?.closest('.sav-summary__section');
    if (summaryPhotosSection) {
      summaryPhotosSection.hidden = shouldHideProblemDetailsStep;
    }
    if (this.summaryPhotos) {
      this.summaryPhotos.textContent = shouldHideProblemDetailsStep
        ? ''
        : `${this.formData.photos.length} ${this.getMessage('summaryPhotosAttached', 'photo(s) ajoutee(s)')}`;
    }
  }

  resetRequestFlow() {
    this.formData = {
      channel: '',
      verified: false,
      serviceType: '',
      serviceReason: '',
      locale: '',
      problemCategory: '',
      problemDescription: '',
      selectedProduct: null,
      selectedExchangeProduct: null,
      photos: [],
      returnAddress1: '',
      returnAddress2: '',
      returnStreet: '',
      returnCity: '',
      returnZip: '',
      returnProvince: '',
      returnCountry: '',
      returnPhone: '',
      returnPhoneCountry: ''
    };
    this.availableProducts = [];
    this.productsLoadState = 'idle';
    this.exchangeCandidates = [];
    this.exchangeCandidatesLoadState = 'idle';

    this.clearErrors();

    if (this.orderNumberInput) this.orderNumberInput.value = '';
    if (this.emailInput) this.emailInput.value = '';
    if (this.boutiqueOrderInput) this.boutiqueOrderInput.value = '';
    if (this.warrantyCardInput) this.warrantyCardInput.value = '';
    if (this.serviceTypeInputs?.length) {
      this.serviceTypeInputs.forEach((input) => {
        input.checked = false;
      });
    }
    this.applyServiceTypeAvailability();
    this.syncChannelBlockVisibility();
    this.applyForcedChannelForServiceType();
    if (this.serviceReasonSelect) {
      this.serviceReasonSelect.innerHTML = '<option value="">' + this.escapeHtml(this.getMessage('serviceReasonPlaceholder', 'Sélectionnez un motif')) + '</option>';
      this.serviceReasonSelect.value = '';
    }
    if (this.problemCategorySelect) this.problemCategorySelect.selectedIndex = 0;
    if (this.problemDescriptionTextarea) this.problemDescriptionTextarea.value = '';
    if (this.returnAddress1Input) this.returnAddress1Input.value = '';
    if (this.returnAddress2Input) this.returnAddress2Input.value = '';
    if (this.returnCityInput) this.returnCityInput.value = '';
    if (this.returnZipInput) this.returnZipInput.value = '';
    if (this.returnProvinceInput) this.returnProvinceInput.value = '';
    if (this.returnCountryInput) this.returnCountryInput.value = '';
    if (this.returnPhoneInput) this.returnPhoneInput.value = '';
    if (this.photoInput) this.photoInput.value = '';
    if (this.photoPreviews) this.photoPreviews.innerHTML = '';
    if (this.requestNumber) this.requestNumber.textContent = '';
    if (this.returnPhoneCountryInput) this.returnPhoneCountryInput.value = 'FR+33';


    if (this.otpInputs) {
      this.otpInputs.forEach((input) => {
        input.value = '';
      });
    }
    if (this.otpSection) {
      this.otpSection.style.display = 'none';
    }

    if (this.productsList) {
      this.productsList.innerHTML = this.initialProductsMarkup;
    }
    if (this.exchangeProductsList) {
      this.exchangeProductsList.innerHTML = this.initialExchangeProductsMarkup;
    }

    if (this.faqSection) {
      this.faqSection.style.display = 'none';
      this.faqSection.classList.remove('is-static');
    }
    if (this.faqItems) {
      this.faqItems.innerHTML = '';
    }

    if (this.channelInputs?.length) {
      this.channelInputs.forEach((input) => {
        input.checked = false;
      });
      this.handleChannelChange();
    }

    this.steps.forEach((step) => {
      step.style.display = '';
      step.hidden = false;
    });

    if (this.loadingState) this.loadingState.classList.remove('is-visible');
    if (this.successState) this.successState.classList.remove('is-visible');

    if (this.stepAnimationTimeout) {
      window.clearTimeout(this.stepAnimationTimeout);
      this.stepAnimationTimeout = null;
    }

    this.currentStep = 0;
    this.syncServiceReasonField();
    this.syncConditionalStepVisibility();
    this.showStep(this.currentStep);
    this.clearFeedback();
    this.requestVisualRefresh();
  }

  async submitForm() {
    try {
      const shouldSkipReturnAddress = this.shouldSkipReturnAddressStep();

      this.setLoading(true);
      this.actions.style.display = 'none';
      this.steps[this.currentStep].style.display = 'none';
      this.loadingState.classList.add('is-visible');

      // Upload photos first
      const photoUrls = await this.uploadPhotos();
      console.log('Final photoUrls array:', photoUrls); // should be ['key1', 'key2', ...]


      // Create order with details + return address + images
      const createOrderPayload = {
        order_id: this.formData.orderId || '',
        email: this.userEmail || this.formData.email || '',
        purchaseChannel: this.formData.channel,
        locale: this.formData.locale || this.getStorefrontLocale(),
        serviceType: this.formData.serviceType,
        serviceReason: this.formData.serviceReason,
        problemCategory: this.formData.problemCategory,
        problemDescription: this.formData.problemDescription,
        selectedProduct: this.formData.selectedProduct,
        exchangeProduct: this.formData.selectedExchangeProduct,
        returnAddress1: shouldSkipReturnAddress ? '' : this.formData.returnAddress1,
        returnAddress2: shouldSkipReturnAddress ? '' : this.formData.returnAddress2,
        returnCity: shouldSkipReturnAddress ? '' : this.formData.returnCity,
        returnZipCode: shouldSkipReturnAddress ? '' : this.formData.returnZip,
        returnProvince: shouldSkipReturnAddress ? '' : this.formData.returnProvince,
        returnCountry: shouldSkipReturnAddress ? '' : this.formData.returnCountry,
        images: photoUrls,
        sav_online_order_number: this.formData.channel === 'online'
          ? this.formData.orderNumber
          : (this.formData.warrantyCard || this.formData.boutiqueOrder),
        sku: this.formData.selectedProduct?.sku || '',
        return_phone_number: shouldSkipReturnAddress ? '' : (this.formData.returnPhone || ''),
        return_phone_country: shouldSkipReturnAddress ? '' : (this.formData.returnPhoneCountry || '')
      };
      this.logApiRequest('create-sav-order', createOrderPayload);
      const createOrderResponse = await fetch(`${this.API_BASE_URL}/sav-order/create-sav-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createOrderPayload)
      });

      const createOrderData = await createOrderResponse.json().catch(() => ({}));
      this.logApiResponse('create-sav-order', createOrderResponse, createOrderData);

      if (!createOrderResponse.ok) {
        throw new Error(createOrderData.message || this.getMessage('submissionFailed', "L'envoi de la demande a échoué. Veuillez réessayer."));
      }

      // Show success
      this.loadingState.classList.remove('is-visible');
      this.successState.classList.add('is-visible');
      this.requestNumber.textContent = this.formatCaseNumber(
        createOrderData.case_id || createOrderData.case_number || createOrderData.order_name
      );
    
    } catch (error) {
      console.error('Submit error:', error);
      this.setLoading(false);
      this.loadingState.classList.remove('is-visible');
      this.steps[this.currentStep].style.display = 'block';
      this.actions.style.display = 'flex';
      this.showNotification(
        error.message || this.getMessage('submissionFailed', "L'envoi de la demande a échoué. Veuillez réessayer."),
        'error',
        { target: 'formActions' }
      );
    }
  }

async uploadPhotos() {
  if (this.formData.photos.length === 0) {
    this.logApiRequest('upload-images', { files: [], orderId: this.formData.orderId || '' });
    return [];
  }

  const formData = new FormData();
  formData.append('orderId', this.formData.orderId || '');

  this.formData.photos.forEach((file, index) => {
    formData.append(`file${index}`, file);
  });

  console.log('Uploading', this.formData.photos.length, 'files in one request');
  this.logApiRequest('upload-images', formData);

  const response = await fetch(`${this.API_BASE_URL}/sav-order/upload-images`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(this.getMessage('photoUploadFailed', "L'envoi des photos a échoué."));
  }

  const data = await response.json();
  this.logApiResponse('upload-images', response, data);
  console.log('Upload response:', data);

  // data is an array of { Key, Id } objects — one per file
  const keys = Array.isArray(data) ? data.map((item) => item.Key) : [];
  console.log('Photo keys to submit:', keys);
  return keys;
}

  // Utility methods

  // The success screen shows only the bare request number: any leading "#",
  // "SAV" or "CASE" prefix returned by the API (e.g. "#SAV-10550840") is
  // stripped so the customer only sees the digits.
  formatCaseNumber(rawValue) {
    let value = rawValue == null ? '' : String(rawValue).trim();
    value = value
      .replace(/^[#\s]+/, '')
      .replace(/^(CASE|SAV)(?=[-_\s\d])[-_\s]*/i, '')
      .replace(/^[#\s]+/, '');
    if (!value) value = String(Date.now());
    return value;
  }

  escapeHtml(value) {
    const stringValue = value == null ? '' : String(value);
    return stringValue
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  formatMultilineText(value) {
    return this.escapeHtml(value).replace(/\n/g, '<br>');
  }

  async initAddressAutocomplete() {
    if (!this.returnAddress1Input || !this.config.addressAutocompleteKey) {
      return;
    }

    try {
      await this.loadGooglePlacesAutocomplete();
      this.setupAddressAutocomplete();
    } catch (error) {
      console.warn('Adresse autocomplete unavailable:', error);
    }
  }

  async loadGooglePlacesAutocomplete() {
    if (window.google?.maps?.places) {
      return;
    }

    if (SavForm.googlePlacesPromise) {
      return SavForm.googlePlacesPromise;
    }

    if (!window.__savGoogleAuthFailureHooked) {
      window.__savGoogleAuthFailureHooked = true;
      const previousAuthFailureHandler = window.gm_authFailure;
      window.gm_authFailure = () => {
        if (typeof previousAuthFailureHandler === 'function') {
          previousAuthFailureHandler();
        }
        console.error(
          'Google Maps authentication failed. Check API key, enabled APIs, billing, and referrer restrictions.'
        );
      };
    }

    const locale = (document.documentElement.lang || 'en').split('-')[0];
    const scriptSrc = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.config.addressAutocompleteKey)}&libraries=places&language=${encodeURIComponent(locale)}`;

    SavForm.googlePlacesPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google Places API'));
      document.head.appendChild(script);
    });

    return SavForm.googlePlacesPromise;
  }

  setupAddressAutocomplete() {
    if (!window.google?.maps?.places?.Autocomplete) {
      console.warn(
        'Google Places Autocomplete constructor unavailable. Verifier Places API setup for this project.'
      );
      return;
    }

    if (!this.returnAddress1Input || this.addressAutocomplete) {
      return;
    }

    this.returnAddress1Input.setAttribute('autocomplete', 'street-address');

    this.addressAutocomplete = new window.google.maps.places.Autocomplete(this.returnAddress1Input, {
      types: ['address'],
      fields: ['address_components', 'formatted_address', 'name']
    });

    this.addressAutocomplete.addListener('place_changed', () => {
      this.handleAddressAutocompleteSelection();
    });
  }

  handleAddressAutocompleteSelection() {
    if (!this.addressAutocomplete) return;

    const place = this.addressAutocomplete.getPlace();
    const components = Array.isArray(place?.address_components) ? place.address_components : [];
    if (!components.length) return;

    const parts = this.extractAddressParts(components);
    const line1 = [parts.streetNumber, parts.route].filter(Boolean).join(' ').trim() || parts.premise || place.name || '';

    // Une nouvelle sélection remplace l'adresse complète : les champs que la
    // nouvelle adresse ne renseigne pas sont vidés pour ne jamais mélanger deux adresses.
    this.assignAddressField(this.returnAddress1Input, line1, { dispatchEvents: false, allowEmpty: true });
    this.assignAddressField(this.returnAddress2Input, parts.subpremise, { allowEmpty: true });
    this.assignAddressField(
      this.returnCityInput,
      parts.locality || parts.postalTown || parts.sublocality || parts.adminLevel2,
      { allowEmpty: true }
    );
    this.assignAddressField(this.returnZipInput, parts.postalCode, { allowEmpty: true });
    this.assignAddressField(this.returnProvinceInput, parts.adminLevel1, { allowEmpty: true });
    this.assignAddressField(this.returnCountryInput, parts.country, { allowEmpty: true });
    ['return-address-1', 'return-address-2', 'return-city', 'return-zip', 'return-province', 'return-country']
      .forEach((field) => this.clearFieldError(field));
    this.closeAddressAutocompleteDropdown();
  }

  extractAddressParts(components) {
    const pick = (type, useShort = false) => {
      const component = components.find((entry) => Array.isArray(entry.types) && entry.types.includes(type));
      if (!component) return '';
      return useShort ? component.short_name || component.long_name || '' : component.long_name || component.short_name || '';
    };

    return {
      streetNumber: pick('street_number'),
      route: pick('route'),
      subpremise: pick('subpremise'),
      premise: pick('premise'),
      locality: pick('locality'),
      postalTown: pick('postal_town'),
      sublocality: pick('sublocality') || pick('sublocality_level_1'),
      adminLevel2: pick('administrative_area_level_2'),
      adminLevel1: pick('administrative_area_level_1', true),
      postalCode: pick('postal_code'),
      country: pick('country')
    };
  }

  closeAddressAutocompleteDropdown() {
    if (!this.returnAddress1Input) return;

    window.requestAnimationFrame(() => {
      this.returnAddress1Input.blur();
    });
  }

  assignAddressField(input, value, options = {}) {
    const { overwrite = true, dispatchEvents = true, allowEmpty = false } = options;
    if (!input) return;
    const nextValue = value || '';
    if (!nextValue && !allowEmpty) return;
    if (!overwrite && input.value.trim()) return;

    if (input.tagName === 'SELECT' && nextValue) {
      const hasOption = Array.from(input.options).some((option) => option.value === nextValue);
      if (!hasOption) {
        input.add(new Option(nextValue, nextValue));
      }
    }

    input.value = nextValue;
    if (dispatchEvents) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setLoading(isLoading) {
    this.nextBtn.disabled = isLoading;
    if (this.prevBtn) this.prevBtn.disabled = isLoading;
    if (this.faqCancelBtn) this.faqCancelBtn.disabled = isLoading;
    if (this.faqContinueBtn) this.faqContinueBtn.disabled = isLoading;
  }

  isApiSuccess(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  resolveVerificationErrorMessage(rawMessage, fallbackMessage, statusCode = 0) {
    const message = typeof rawMessage === 'string' ? rawMessage.trim() : '';
    const normalized = this.normalizeErrorCasing(message);
    const isOnline = this.formData.channel === 'online';
    const isOrderNotFound =
      /^order\s+not\s+found\.?$/i.test(message) ||
      /commande.*(introuvable|non\s+trouv)/i.test(message) ||
      (isOnline && statusCode === 404);

    if (isOrderNotFound) {
      return this.getMessage('orderNotFound', 'Commande introuvable. Vérifiez le numéro saisi.');
    }

    return normalized || fallbackMessage;
  }

  normalizeErrorCasing(message) {
    if (!message) return '';
    const cleaned = String(message).trim().replace(/\s+/g, ' ');
    const normalized = cleaned.toLowerCase();

    if (normalized === 'invalid or expired verification code try resend') {
      return 'Invalid or expired verification code. Try resend.';
    }

    if (normalized === 'order not found') {
      return 'Order not found';
    }

    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  showError(fieldName, message) {
    this.clearFeedback();

    const errorEl = this.element.querySelector(`[data-error="${fieldName}"]`);
    if (errorEl) {
      const normalizedMessage = this.normalizeErrorCasing(message);
      errorEl.textContent = normalizedMessage || message;
      errorEl.classList.add('is-visible');

      // Add error class to input
      const input = errorEl.previousElementSibling;
      const isFormControl = input && (
        input.matches('input, select, textarea') ||
        input.classList.contains('sav-form__input') ||
        input.classList.contains('sav-form__select') ||
        input.classList.contains('sav-form__textarea')
      );
      if (isFormControl) {
        input.classList.add('is-error');
        const errorColor = this.getStyleValue('--sav-error', '#800000');
        input.style.borderColor = errorColor;
      }
    }
  }

  clearErrors() {
    const borderSoft = this.getStyleValue('--sav-lux-border-soft', '#dfdfdf');
    this.element.querySelectorAll('.sav-form__error').forEach(errorEl => {
      errorEl.textContent = '';
      errorEl.classList.remove('is-visible');
    });

    this.element.querySelectorAll('.sav-form__input, .sav-form__select, .sav-form__textarea').forEach(input => {
      input.classList.remove('is-error');
      input.style.borderColor = borderSoft;
      input.style.boxShadow = 'none';
    });
  }

  getFeedbackElement(target = 'formActions') {
    return this.feedbackTargets?.[target] || null;
  }

  clearFeedback(target = null) {
    const targets = target
      ? [target]
      : Object.keys(this.feedbackTargets || {});

    targets.forEach((key) => {
      const feedbackEl = this.getFeedbackElement(key);
      if (!feedbackEl) return;

      const timer = this.feedbackHideTimers[key];
      if (timer) {
        window.clearTimeout(timer);
        delete this.feedbackHideTimers[key];
      }

      feedbackEl.textContent = '';
      feedbackEl.classList.remove('is-visible', 'is-info', 'is-success', 'is-error');
      feedbackEl.hidden = true;
    });
  }

  getStyleValue(name, fallback = '') {
    if (!this.section || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
      return fallback;
    }
    const value = window.getComputedStyle(this.section).getPropertyValue(name);
    return value && value.trim() ? value.trim() : fallback;
  }

  showNotification(message, type = 'info', options = {}) {
    const target = options.target || 'formActions';
    const feedbackEl = this.getFeedbackElement(target);
    if (!message || !feedbackEl) {
      return;
    }

    const kind = ['success', 'error', 'info'].includes(type) ? type : 'info';
    const normalizedMessage = this.normalizeErrorCasing(message);

    this.clearFeedback(target);
    feedbackEl.textContent = normalizedMessage || message;
    feedbackEl.classList.add('is-visible', `is-${kind}`);
    feedbackEl.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    feedbackEl.setAttribute('aria-live', kind === 'error' ? 'assertive' : 'polite');
    feedbackEl.hidden = false;

    if (kind !== 'error') {
      this.feedbackHideTimers[target] = window.setTimeout(() => {
        this.clearFeedback(target);
      }, 4500);
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  maskEmail(email) {
    if (!email) return '';
    const [local, domain] = email.split('@');
    const maskedLocal = local.substring(0, 2) + '***' + local.substring(local.length - 1);
    return `${maskedLocal}@${domain}`;
  }
}

// Initialize all SAV forms on the page
document.addEventListener('DOMContentLoaded', () => {
  const savForms = document.querySelectorAll('[data-sav-form]');
  savForms.forEach(form => {
    new SavForm(form);
  });
});

// Re-initialize on Shopify section reload (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const savForm = event.target.querySelector('[data-sav-form]');
    if (savForm) {
      new SavForm(savForm);
    }
  });
}
